import { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconSparkles, IconLoader2, IconArrowLeft, IconArrowRight, IconCheck,
  IconWand, IconFileText, IconDownload, IconPrinter, IconCopy, IconRefresh,
  IconX, IconPlus, IconPhotoPlus, IconCloudUpload, IconBold, IconItalic, IconUnderline,
  IconAlignLeft, IconAlignCenter, IconAlignRight, IconSettings, IconCircleCheckFilled, IconListDetails,
  IconPencil,
} from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { useAuthStore } from '../../../store/authStore';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { notifications } from '@/lib/notifications';
import aiDocumentService from '../../../services/aiDocumentService';
import templateSetService from '../../../services/templateSetService';
import LimitReachedModal from '@/components/common/LimitReachedModal';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import DatePickerField from '@/components/common/DatePickerField';
import { getTypes, getSchemas, getChips, toBanglaDigits } from './aiDocShared';
import { lexicalToHtml } from '../../../utils/lexicalToHtml';
import { banglishToBangla } from '../tools/text/banglish-typing/banglishTypingEngine';
import { resolveDocFields, getDocPersons } from './aiDocFields';
import { isPdf, pdfToImages } from './pdfToImages';
import ScanCropDialog from './ScanCropDialog';
import BlockEditor, { DocPreviewCanvas, FontSelect, CustomSelect, HtmlDocEditor } from './BlockEditor';
import { downloadDocPdf, openPrint, blocksToText, htmlToText, sanitizeDocHtml, PAPER_SIZES } from '../../../utils/aiDocExport';
import { mapOutsideMath } from '../../../utils/aiDocMath';
import PdfEditorModal from '../../../features/pdf-editor/PdfEditorModal';
import { layoutToPdf } from '../../../features/pdf-editor/engine/layoutToPdf';
import { docToObjects, objectLayoutToPdf, serializeEditorLayout, blankDocFile } from '../../../features/pdf-editor/engine/docToPdf';
import { usePdfEditorStore } from '../../../features/pdf-editor/state/usePdfEditorStore';

// Per-document-type icon from public/images/document-icons/<id>.svg. When a type has
// no icon it falls back to the shared default.svg (a generic document glyph), and only
// to the Tabler icon if even that is missing.
function DocTypeIcon({ typeId, size = 38 }) {
  const srcs = [typeId && `/images/document-icons/${typeId}.svg`, '/images/document-icons/default.svg'].filter(Boolean);
  const [stage, setStage] = useState(0);
  if (stage >= srcs.length) return <IconFileText size={Math.round(size * 0.62)} className="text-primary" />;
  return (
    <img
      key={srcs[stage]}
      src={srcs[stage]}
      alt=""
      loading="lazy"
      onError={() => setStage((s) => s + 1)}
      style={{ width: size, height: size, objectFit: 'contain' }}
    />
  );
}

function Btn({ variant = 'primary', size = 'md', leftIcon, rightIcon, loading, disabled, className = '', children, ...props }) {
  const base =
    'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors focus:outline-none disabled:opacity-50 disabled:pointer-events-none';
  const sizes = { sm: 'px-3 py-1.5 text-sm', md: 'px-4 py-2 text-sm', lg: 'px-5 py-2.5 text-[15px]' };
  const variants = {
    primary: 'bg-primary hover:bg-primary/90 text-white',
    default: 'bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-700',
    light: 'bg-primary/10 dark:bg-primary/20 text-primary hover:bg-primary/20',
    ghost: 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800',
  };
  return (
    <button className={`${base} ${sizes[size]} ${variants[variant]} ${className}`} disabled={disabled || loading} {...props}>
      {loading ? <IconLoader2 size={16} className="animate-spin" /> : leftIcon}
      {children}
      {rightIcon}
    </button>
  );
}


const toISODate = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Seed every date field with today's date so it is never left blank; OCR / AI
// auto-fill overwrite it when a real value is available.
function dateDefaults(plan) {
  const out = {};
  const today = toISODate(new Date());
  (plan?.fields || []).forEach((f) => { if (f && f.type === 'date') out[f.id] = today; });
  return out;
}

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

// Today in DD-MM-YYYY (ASCII; bn conversion happens later) for the {today} token.
function todayStr() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()}`;
}

// Canonical NID ids (values a full NID scan returns). Fields planned with these exact
// ids are filled straight from the scan; anything else is a form's own manual field.
const NID_CANON_IDS = new Set(['name_bn', 'name_en', 'father_name', 'mother_name', 'date_of_birth', 'nid_number', 'blood_group', 'village', 'post_office', 'upazila', 'district']);

// NID value → label matchers (most-specific first: parents & DOB before the generic
// "name" catch-all). `keys` are the canonical values to pull from; `not` skips obvious
// non-person labels (institution/office names). Used to fill a form's OWN manual fields
// (custom ids like "অনুবেদনকারীর নাম") from an NID scan by matching their labels.
const NID_LABEL_MATCHERS = [
  { keys: ['father_name'],        re: /পিতা|বাবা|father/i },
  { keys: ['mother_name'],        re: /মাতা|মায়ের|mother/i },
  { keys: ['date_of_birth'],      re: /জন্ম.?তারিখ|date\s*of\s*birth|\bdob\b/i },
  { keys: ['nid_number'],         re: /পরিচয়পত্র|এনআইডি|জাতীয়\s*পরিচয়|national\s*id|\bnid\b/i },
  { keys: ['blood_group'],        re: /রক্ত|blood/i },
  // Separate address parts (before the generic ঠিকানা catch-all) so a form that splits
  // the address into গ্রাম/ডাকঘর/উপজেলা/জেলা fields still fills each from the NID scan.
  { keys: ['post_office'],        re: /ডাক.?ঘর|post\s*office/i },
  { keys: ['upazila'],            re: /উপজেলা|থানা|upazila|thana/i },
  { keys: ['district'],           re: /জেলা|district/i },
  { keys: ['village'],            re: /গ্রাম|মহল্লা|village|road|রাস্তা/i },
  { keys: ['name_bn', 'name_en'], re: /নাম|name/i, not: /প্রতিষ্ঠান|কোম্পানি|অফিস|দপ্তর|ব্যাংক|company|office|institution|firm|bank|পিতা|মাতা|father|mother|গ্রাম|ডাক.?ঘর|উপজেলা|থানা|জেলা|রাস্তা|road|village|district|post\s*office/i },
  { keys: ['__address__'],        re: /ঠিকানা|address/i },
];

// Build { fieldId: value } fills from a full NID scan (`raw`, canonical ids) for the doc's
// OWN fields, matched by label. Only the FIRST still-EMPTY field per NID value is filled,
// so a second same-kind field (e.g. the counter-signer's name) stays blank rather than
// duplicated — and a later scan (another NID) can fill that next empty slot.
function mapNidByLabel(raw, docFields, currentFields) {
  const out = {};
  const address = ['village', 'post_office', 'upazila', 'district']
    .map((k) => (raw[k] != null ? String(raw[k]).trim() : '')).filter(Boolean).join(', ');
  for (const m of NID_LABEL_MATCHERS) {
    const value = m.keys.includes('__address__')
      ? address
      : (m.keys.map((k) => raw[k]).find((v) => v != null && String(v).trim()) || '');
    if (!String(value).trim()) continue;
    const target = docFields.find((fd) => {
      if (out[fd.id] != null || NID_CANON_IDS.has(fd.id)) return false;
      if (String(currentFields[fd.id] ?? '').trim()) return false; // never overwrite
      const label = String(fd.label || '');
      return m.re.test(label) && !(m.not && m.not.test(label));
    });
    if (target) out[target.id] = value;
  }
  return out;
}

// Common Bangladeshi name markers → religion (only clear cases; else left blank to edit).
const NAME_MUSLIM_RE = /মোঃ|মোহাম্মদ|মোহাম্মাদ|মুহাম্মদ|আব্দুল|আঃ|খাতুন|বেগম|বিবি|হোসেন|হোসাইন|রহমান|ইসলাম|উদ্দিন|মিয়া|\bmd\.?\b|mohammad|muhammad|abdul|khatun|begum|hossain|hussain|rahman|islam|uddin|miah/i;
const NAME_HINDU_RE = /শ্রী|কুমার|চন্দ্র|দেবী|রানী|ঘোষ|মন্ডল|বিশ্বাস|সরকার(?!\s*কর্তৃপক্ষ)|নাথ|দাস|রায়|সেন\b|\bsri\b|kumar|chandra|devi|ghosh|mondal|biswas|\bdas\b|\broy\b|\bsen\b|\bnath\b/i;
const inferReligion = (name, isBn) => {
  const s = String(name || '');
  if (NAME_MUSLIM_RE.test(s)) return isBn ? 'ইসলাম' : 'Islam';
  if (NAME_HINDU_RE.test(s)) return isBn ? 'হিন্দু' : 'Hindu';
  return '';
};
// After a scan fills a person's identity, pre-fill the obvious DERIVED fields the user would
// otherwise type: nationality (→ বাংলাদেশী for BD docs) and religion (inferred from the name).
// Only fills EMPTY fields, scoped to the scanned person for multi-party docs — always editable.
function smartDefaults(docFields, currentFields, name, personKey, isBn) {
  const out = {};
  const nationality = isBn ? 'বাংলাদেশী' : 'Bangladeshi';
  const religion = inferReligion(name, isBn);
  for (const fd of docFields) {
    if (personKey ? fd.person !== personKey : !!fd.person) continue; // scope to the right owner
    if (String(currentFields[fd.id] ?? '').trim()) continue;         // never overwrite
    const label = String(fd.label || '');
    if (/জাতীয়তা|নাগরিকত্ব|nationality|citizenship/i.test(label)) out[fd.id] = nationality;
    else if (religion && /ধর্ম|religion/i.test(label)) out[fd.id] = religion;
  }
  return out;
}

// Normalise a date value for insertion: ISO (YYYY-MM-DD) → DD-MM-YYYY; else as-is.
function formatDateValue(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v || '').trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}` : String(v || '');
}

// Client-side token replace — mirrors the backend so the finished document appears
// instantly (no wait) when the cached template is ready. Empty field → [Label].
function applyTemplate(blocks, defs, fieldValues, lang) {
  const labels = {};
  const types = {};
  defs.forEach((d) => { labels[d.id] = d.label; types[d.id] = d.type; });
  const today = todayStr();
  return (Array.isArray(blocks) ? blocks : []).map((b) => {
    // Math ($...$) is skipped: its TeX braces (\frac{a}{b}) are not field tokens.
    let text = mapOutsideMath(String(b.text || ''), (seg) => seg.replace(/\{([a-zA-Z0-9_]+)\}/g, (m, id) => {
      if (id === 'today') return today;
      let v = fieldValues && fieldValues[id] != null ? String(fieldValues[id]).trim() : '';
      if (v && types[id] === 'date') v = formatDateValue(v);
      if (v) return v;
      return `[${labels[id] || id}]`;
    }), { html: false });
    if (lang === 'bn') text = toBanglaDigits(text);
    return { ...b, text };
  });
}

// HTML variant of applyTemplate: replace {token}s inside an HTML string. Only the
// INJECTED VALUES are HTML-escaped + Bangla-digit converted — never the template's own
// markup (converting the whole string would corrupt colspan="2", #333, widths, etc.).
function applyTemplateHtml(htmlTemplate, defs, fieldValues, lang) {
  const labels = {};
  const types = {};
  (defs || []).forEach((d) => { labels[d.id] = d.label; types[d.id] = d.type; });
  const bn = lang === 'bn';
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const today = bn ? toBanglaDigits(todayStr()) : todayStr();
  // Math ($...$ / $$...$$) is skipped: its TeX braces (\frac{12}{y}) are NOT field tokens.
  return mapOutsideMath(String(htmlTemplate || ''), (seg) => seg.replace(/\{([a-zA-Z0-9_]+)\}/g, (m, id) => {
    if (id === 'today') return today;
    let v = fieldValues && fieldValues[id] != null ? String(fieldValues[id]).trim() : '';
    if (v && types[id] === 'date') v = formatDateValue(v);
    if (!v) return `[${esc(labels[id] || id)}]`;
    v = esc(v);
    return bn ? toBanglaDigits(v) : v;
  }));
}

// Skeleton "paper" shown in the preview the instant the user hits Generate, so the
// document feels like it is being drafted rather than the user being stuck on step 2.
const GEN_LINES = [
  ['70%', '92%', '88%', '60%'],
  ['95%', '84%', '90%', '48%'],
  ['82%', '93%', '70%'],
];
function GeneratingPreview({ lang, name, t }) {
  const isBn = lang === 'bn';
  return (
    <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 overflow-hidden bg-white dark:bg-zinc-900 flex flex-col">
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-200 dark:border-zinc-700">
        <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate max-w-[55%]">{name || (isBn ? 'ডকুমেন্ট' : 'Document')}</p>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-primary">
          <IconLoader2 size={12} className="animate-spin" />{isBn ? 'তৈরি হচ্ছে…' : 'Generating…'}
        </span>
      </div>
      <div
        className="flex-1 overflow-hidden p-4 md:p-8 flex justify-center"
        style={{ background: '#fafafa', backgroundImage: 'radial-gradient(#d6d3ce 1px, transparent 1px)', backgroundSize: '16px 16px' }}
      >
        <div className="relative w-full max-w-[560px] aspect-[1/1.414] bg-white rounded shadow-xl border border-zinc-200 overflow-hidden">
          <style>{`@keyframes adocGenSweep{0%{transform:translateY(-140%)}100%{transform:translateY(160%)}}`}</style>
          <div
            className="absolute inset-x-0 h-28 z-10 pointer-events-none"
            style={{ animation: 'adocGenSweep 1.9s ease-in-out infinite', background: 'linear-gradient(to bottom, transparent, rgba(230,138,0,0.16), transparent)' }}
          />
          <div className="p-8 md:p-12 space-y-7">
            <div className="h-5 w-2/3 mx-auto rounded bg-zinc-200 animate-pulse" />
            <div className="h-2.5 w-1/3 mx-auto rounded bg-zinc-100 animate-pulse" />
            {GEN_LINES.map((grp, gi) => (
              <div key={gi} className="space-y-2.5">
                {grp.map((w, i) => (
                  <div key={i} className="h-3 rounded bg-zinc-200/80 animate-pulse" style={{ width: w, animationDelay: `${(gi * 4 + i) * 80}ms` }} />
                ))}
              </div>
            ))}
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-white/85 border border-zinc-200 shadow-lg backdrop-blur-sm">
              <IconLoader2 size={18} className="animate-spin text-primary" />
              <span className="text-sm font-medium text-zinc-700">
                {t('aiDocuments.generatingDoc', { defaultValue: 'AI is drafting your document…' })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Scanner drawn over a document while the AI reads it: a glowing beam sweeping up and
// down, pulsing corner brackets and a faint orange wash. Pure Tailwind + the page keyframes.
function ScanOverlay({ small = false }) {
  // `small` = thumbnail-sized (the "fill from a document" strip): thinner corners + beam.
  const corner = `absolute border-primary ${small ? 'w-3 h-3' : 'w-6 h-6'}`;
  const bw = small ? '2px' : '3px';
  return (
    <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
      <div className="absolute inset-0 bg-primary/[0.06]" />
      <div className={`absolute inset-x-0 -translate-y-1/2 ${small ? 'h-6' : 'h-16'}`} style={{ animation: `adocBeam ${small ? '1.2s' : '1.8s'} ease-in-out infinite alternate` }}>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/25 to-transparent" />
        <div className="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 bg-primary shadow-[0_0_14px_3px] shadow-primary/60" />
      </div>
      <div className={`absolute animate-pulse ${small ? 'inset-1' : 'inset-2'}`}>
        <span className={`${corner} top-0 left-0 rounded-tl-lg`} style={{ borderTopWidth: bw, borderLeftWidth: bw }} />
        <span className={`${corner} top-0 right-0 rounded-tr-lg`} style={{ borderTopWidth: bw, borderRightWidth: bw }} />
        <span className={`${corner} bottom-0 left-0 rounded-bl-lg`} style={{ borderBottomWidth: bw, borderLeftWidth: bw }} />
        <span className={`${corner} bottom-0 right-0 rounded-br-lg`} style={{ borderBottomWidth: bw, borderRightWidth: bw }} />
      </div>
    </div>
  );
}

// Slim indeterminate progress bar (scan / planning in progress).
function IndeterminateBar({ className = '' }) {
  return (
    <div className={`h-1 rounded-full bg-primary/15 overflow-hidden ${className}`}>
      <div className="h-full w-2/5 rounded-full bg-primary" style={{ animation: 'adocIndet 1.3s ease-in-out infinite' }} />
    </div>
  );
}

// Shown while the AI plan is being prepared (reference-document analysis or a typed
// request): a header, a step checklist that advances on a timer (the last step keeps
// spinning until the plan lands) and a shimmering preview of the form being built.
function AnalysingPanel({ t, reference, stage = 'read', variant = 'plan', previewLabels = null, bare = false }) {
  const scan = variant === 'scan';
  const steps = scan
    ? [
      t('aiDocuments.scanStepDetect', { defaultValue: 'Detecting the document' }),
      t('aiDocuments.scanStepRead', { defaultValue: 'Reading the name & details' }),
      t('aiDocuments.scanStepMatch', { defaultValue: "Matching them to your form's fields" }),
      t('aiDocuments.scanStepFill', { defaultValue: 'Filling in the fields' }),
    ]
    : reference && stage === 'read'
    // A sample photo is read first, before anything is known about it — so these words fit ANY
    // document (a slip, a letter, a form). Form wording only appears once it turns out to be one.
    ? [
      t('aiDocuments.analyseStepImage', { defaultValue: 'Reading the image' }),
      t('aiDocuments.analyseStepText', { defaultValue: 'Recognising the text' }),
      t('aiDocuments.analyseStepTables', { defaultValue: 'Detecting tables & sections' }),
    ]
    : reference
    ? [
      t('aiDocuments.analyseStepFields', { defaultValue: 'Finding the fields to fill' }),
      t('aiDocuments.analyseStepBlanks', { defaultValue: 'Matching the blanks to fields' }),
      t('aiDocuments.analyseStepBuild', { defaultValue: 'Building your form' }),
    ]
    : [
      t('aiDocuments.analyseStepRequest', { defaultValue: 'Understanding your request' }),
      t('aiDocuments.analyseStepFields', { defaultValue: 'Finding the fields to fill' }),
      t('aiDocuments.analyseStepBuild', { defaultValue: 'Building your form' }),
    ];
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((a) => Math.min(a + 1, steps.length - 1)), 2400);
    return () => clearInterval(id);
  }, [steps.length]);
  return (
    <div
      className={bare ? '' : 'rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4 sm:p-6 shadow-sm'}
      style={{ animation: bare ? 'adocPanelIn .5s cubic-bezier(0.22,1,0.36,1) both' : 'adocRise .5s cubic-bezier(0.22,1,0.36,1) both' }}
    >
      <div className="flex items-center gap-3">
        <span className="relative inline-flex items-center justify-center w-11 h-11 rounded-xl bg-primary/10 text-primary shrink-0">
          <span aria-hidden="true" className="pointer-events-none absolute -inset-1 rounded-xl bg-primary/25 blur-md" style={{ animation: 'adocAiGlow 2.4s ease-in-out infinite' }} />
          <IconSparkles size={22} className="relative animate-pulse" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-zinc-800 dark:text-zinc-100">
            {scan
              ? t('aiDocuments.scanning', { defaultValue: 'Reading document…' })
              : reference
                ? t('aiDocuments.analysingDoc', { defaultValue: 'Analysing the document…' })
                : t('aiDocuments.planningWait', { defaultValue: 'Preparing the form…' })}
          </p>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">{scan
            ? t('aiDocuments.scanningSub', { defaultValue: 'Hold on — AI is reading every detail' })
            : t('aiDocuments.analysingSub', { defaultValue: 'This usually takes a few seconds' })}</p>
        </div>
      </div>

      {/* Step checklist */}
      <ul className="mt-5 space-y-2.5">
        {steps.map((label, i) => {
          const done = i < active;
          const current = i === active;
          return (
            <li key={label} className="flex items-center gap-2.5" style={{ animation: `adocFieldIn .4s ease-out ${i * 90}ms both` }}>
              <span className="inline-flex items-center justify-center w-5 h-5 shrink-0">
                {done ? (
                  <IconCircleCheckFilled size={20} className="text-emerald-500" />
                ) : current ? (
                  <IconLoader2 size={18} className="text-primary animate-spin" />
                ) : (
                  <span className="w-2 h-2 rounded-full bg-zinc-200 dark:bg-zinc-700" />
                )}
              </span>
              <span className={`text-sm transition-colors duration-300 ${
                done ? 'text-zinc-500 dark:text-zinc-400' : current ? 'font-semibold text-zinc-800 dark:text-zinc-100' : 'text-zinc-400 dark:text-zinc-500'
              }`}>
                {label}
              </span>
            </li>
          );
        })}
      </ul>

      {/* Form preview — skeleton tiles shimmering while the fields are worked out. A scan
          already knows its fields, so it shows their real labels with a shimmering value. */}
      <div className="mt-6 pt-4 border-t border-zinc-100 dark:border-zinc-800">
        <p className="text-[11px] font-medium text-zinc-400 mb-2.5">{reference && stage === 'read'
          ? t('aiDocuments.docPreview', { defaultValue: 'Document preview' })
          : t('aiDocuments.formPreview', { defaultValue: 'Form preview' })}</p>
        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-2 ${bare ? '' : 'lg:grid-cols-3'}`}>
          {(previewLabels && previewLabels.length ? previewLabels.slice(0, 6) : [null, null, null, null, null, null]).map((label, i) => {
            const w = [64, 48, 72, 56, 40, 68][i % 6];
            return (
              <div key={i} className="rounded-lg border border-dashed border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/40 px-3 py-2 space-y-1.5" style={{ animation: `adocFieldIn .45s ease-out ${300 + i * 70}ms both` }}>
                {label
                  ? <p className="text-[11px] leading-4 text-zinc-500 dark:text-zinc-400 truncate">{label}</p>
                  : <div className="h-2 rounded-full bg-zinc-200 dark:bg-zinc-700 animate-pulse" style={{ width: `${w - 20}%`, animationDelay: `${i * 120}ms` }} />}
                <div className="h-2.5 rounded-full bg-primary/15 animate-pulse" style={{ width: `${w}%`, animationDelay: `${i * 120 + 60}ms` }} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// One compact field tile: small label on top, borderless value underneath. Filled tiles
// are solid white with a green check; empty ones are a soft dashed placeholder. `index`
// staggers the entrance so extracted fields cascade in after a scan.
function FieldRow({ f, value, onChange, isBnUi, index = 0, pending = false }) {
  const filled = !!String(value || '').trim();
  const isArea = f.type === 'textarea';
  const ctrl = 'w-full bg-transparent border-0 p-0 text-sm font-medium text-zinc-800 dark:text-zinc-100 placeholder:font-normal placeholder:text-zinc-300 dark:placeholder:text-zinc-600 focus:outline-none focus:ring-0';
  return (
    <div
      className={`relative rounded-lg border px-3 py-1.5 transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15 ${
        filled
          ? 'bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700'
          : pending
            ? 'bg-white dark:bg-zinc-900 border-dashed border-primary/50'
            : 'bg-zinc-50 dark:bg-zinc-800/40 border-dashed border-zinc-200 dark:border-zinc-700'
      } ${isArea ? 'sm:col-span-full' : ''}`}
      style={{ animation: `adocFieldIn .45s cubic-bezier(0.22,1,0.36,1) ${Math.min(index, 18) * 35}ms both` }}
    >
      {/* Waiting to be filled by a running document scan — soft orange pulse */}
      {pending && !filled && <span aria-hidden="true" className="absolute inset-0 rounded-lg bg-primary/10 animate-pulse pointer-events-none" />}
      <span className="flex items-center gap-1 text-[11px] leading-4 text-zinc-500 dark:text-zinc-400">
        <span className="truncate">{f.label}</span>
        {filled && <IconCircleCheckFilled size={12} className="text-emerald-500 shrink-0" />}
      </span>
      {isArea ? (
        <textarea value={value || ''} onChange={(e) => onChange(f.id, e.target.value)} rows={2} placeholder={isBnUi ? 'লিখুন…' : 'Enter…'} className={`${ctrl} resize-y`} />
      ) : f.type === 'date' ? (
        <DatePickerField
          value={value ? new Date(`${value}T00:00:00`) : null}
          onChange={(d) => onChange(f.id, d ? toISODate(d) : '')}
          placeholder={isBnUi ? 'তারিখ বাছুন' : 'Pick a date'}
          clearable
          fromYear={1940}
          toYear={new Date().getFullYear() + 5}
          triggerClassName={`${ctrl} min-h-5`}
        />
      ) : (
        <input type="text" value={value || ''} onChange={(e) => onChange(f.id, e.target.value)} placeholder={isBnUi ? 'লিখুন…' : 'Enter…'} className={ctrl} />
      )}
    </div>
  );
}

// Document-wide font-size scale presets for the right sidebar (multiplies every
// block's size; per-block overrides still apply on top).
const FONT_SIZE_SCALES = [
  { value: 0.9, en: 'Small', bn: 'ছোট' },
  { value: 1, en: 'Normal', bn: 'স্বাভাবিক' },
  { value: 1.1, en: 'Large', bn: 'বড়' },
  { value: 1.25, en: 'Extra large', bn: 'অতিরিক্ত বড়' },
];

const DRAFT_KEY = 'aidoc_draft_id';
// Placeholder NID source shown the moment the user lands on step 2, before the AI
// plan has arrived — uploading against it is queued and re-targeted to the plan's
// first source once planning finishes.
const PENDING_SRC_ID = '__nid';
// Floating action bar shared by every step: soft dark grey, centered, half width (grows to fit its
// buttons; full width on phones).
const footerBase =
  'sticky bottom-3 z-20 mt-3 w-full rounded-2xl border border-zinc-600 bg-zinc-700 dark:border-zinc-700 dark:bg-zinc-800 shadow-xl px-4 py-3 flex items-center gap-3';
const footerCls = `${footerBase} md:w-1/2 md:min-w-fit mx-auto`; // final-document step: centered, half width
const footerWideCls = footerBase; // upload / scan step: spans the full content width
// Ghost buttons sit on the dark bar — light text, subtle white hover.
const footerGhostCls = '!text-zinc-300 hover:!bg-white/10 hover:!text-white';

// Load a data URL / object URL into a decoded <img> (for on-page card detection).
const loadImageEl = (src) =>
  new Promise((resolve, reject) => {
    const im = new window.Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = src;
  });

// Axis-aligned bounding box of a detected card quad → react-image-crop percent rect,
// padded a touch and clamped. Used as the INITIAL (adjustable) crop selection so we
// suggest the card without ever hard-cropping it.
function quadToPercentCrop(quad, nw, nh, padPct = 2) {
  const xs = quad.map((p) => p[0]);
  const ys = quad.map((p) => p[1]);
  let minX = Math.min(...xs);
  let maxX = Math.max(...xs);
  let minY = Math.min(...ys);
  let maxY = Math.max(...ys);
  const px = (maxX - minX) * (padPct / 100);
  const py = (maxY - minY) * (padPct / 100);
  minX = Math.max(0, minX - px);
  minY = Math.max(0, minY - py);
  maxX = Math.min(nw, maxX + px);
  maxY = Math.min(nh, maxY + py);
  return { unit: '%', x: (minX / nw) * 100, y: (minY / nh) * 100, width: ((maxX - minX) / nw) * 100, height: ((maxY - minY) / nh) * 100 };
}

export default function AiDocumentCreate() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, currentLanguage } = useTranslation();
  const { user } = useAuthStore();
  const { selectedOfficeSpace } = useOfficeSpaceStore();
  const isAdmin = user?.role === 'admin';
  const isOperator = !!(selectedOfficeSpace && user && selectedOfficeSpace.ownerId != null && selectedOfficeSpace.ownerId !== user.id);

  const [catalog, setCatalog] = useState(null);
  const [creditCost, setCreditCost] = useState(null);
  const [docLang, setDocLang] = useState(currentLanguage === 'bn' ? 'bn' : 'en');
  const [step, setStep] = useState(0);
  const [selectedType, setSelectedType] = useState(null);
  const [chatText, setChatText] = useState('');
  const [sampleImage, setSampleImage] = useState(null); // optional reference/sample doc image (data URL)
  const [sampleBusy, setSampleBusy] = useState(false);

  const [docId, setDocId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [planStage, setPlanStage] = useState('read'); // sample photo: 'read' (any document) → 'fields' (it is a form)
  const [fields, setFields] = useState({});
  const [collected, setCollected] = useState(() => new Set());
  const [extracting, setExtracting] = useState(null);
  const [sourceImages, setSourceImages] = useState({}); // { sourceId: dataUrl }
  const [dismissedSources, setDismissedSources] = useState(() => new Set()); // source cards the user closed
  const [extractedBySource, setExtractedBySource] = useState({}); // { sourceId: [fieldId] }
  const [revealCount, setRevealCount] = useState({}); // staggered "tap-tap" reveal per source
  const pendingScanRef = useRef(null); // image scanned before the plan arrived
  const revealTimer = useRef(null);

  const [docBlocks, setDocBlocks] = useState([]);
  // Advanced edit: the current block/html doc flattened into the shared PDF editor.
  const [advDoc, setAdvDoc] = useState(null);       // { file, objects }
  const [advOpen, setAdvOpen] = useState(false);
  const [advBuilding, setAdvBuilding] = useState(false);
  const [advSaving, setAdvSaving] = useState(false);
  const [savedLayout, setSavedLayout] = useState(null); // { kind:'objects', pages, objects } once PDF-edited
  const [docHtml, setDocHtml] = useState(null); // HTML-format document (reference/form docs with tables); null → block-format
  const [templateBlocks, setTemplateBlocks] = useState(null); // cached {token} template, fetched in the background
  const [templateHtml, setTemplateHtml] = useState(null); // cached {token} HTML template (form docs)
  const [templateLoading, setTemplateLoading] = useState(false);
  const [fillBusy, setFillBusy] = useState(false); // scanning a supporting doc to fill fields
  const [fillPreview, setFillPreview] = useState(null); // that supporting doc's image, shown with the scanner while it reads
  const [generating, setGenerating] = useState(false);
  const [manualEditing, setManualEditing] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  const [cropState, setCropState] = useState({ open: false, imageSrc: null, sourceId: null });
  const [limitOpen, setLimitOpen] = useState(false);
  const saveTimer = useRef(null);
  const editorRef = useRef(null); // BlockEditor imperative API (sidebar text tools)
  const searchInputRef = useRef(null); // step-0 search box (focused by the "/" shortcut)
  const planSeq = useRef(0); // bumped on each proceed()/startOver() → discards a superseded plan
  const badgeFileRef = useRef(null); // hidden picker triggered by the image-mode start badges
  const badgeBrief = useRef(''); // framing note the active badge applies to the uploaded sample
  const [editingIdx, setEditingIdx] = useState(-1);
  const [paper, setPaper] = useState('a4');
  const [landscape, setLandscape] = useState(false);
  const [globalFont, setGlobalFont] = useState(null); // document-wide font (per-block wins)
  // Page margins: preset id + custom per-side mm. null margins = the legacy defaults.
  const [marginPreset, setMarginPreset] = useState('default'); // default | narrow | wide | custom
  const [customMm, setCustomMm] = useState({ top: 12.7, bottom: 12.7, left: 14.8, right: 14.8 });
  // Exact px margins copied from a template-set doc's own metadata — when set they win over
  // the preset so the generated document matches the source's page margins precisely.
  const [sourceMargins, setSourceMargins] = useState(null);
  const margins = useMemo(() => {
    if (sourceMargins) return sourceMargins;
    const mm2px = (mm) => Math.round(Math.min(70, Math.max(0, Number(mm) || 0)) * 96 / 25.4);
    if (marginPreset === 'narrow') return { top: mm2px(8), bottom: mm2px(8), left: mm2px(8), right: mm2px(8) };
    if (marginPreset === 'wide') return { top: mm2px(25.4), bottom: mm2px(25.4), left: mm2px(25.4), right: mm2px(25.4) };
    if (marginPreset === 'custom') return { top: mm2px(customMm.top), bottom: mm2px(customMm.bottom), left: mm2px(customMm.left), right: mm2px(customMm.right) };
    return null;
  }, [marginPreset, customMm, sourceMargins]);
  const [globalFontScale, setGlobalFontScale] = useState(1); // document-wide font-size scale
  const [edited, setEdited] = useState(false); // any manual change → reveal the Save footer
  const [renamedName, setRenamedName] = useState(null); // step-3 inline rename (overrides the plan name)
  const [editingName, setEditingName] = useState(false); // step-3 doc-name edit mode (double-click to enter)
  const [skipScan, setSkipScan] = useState(false); // step-2: user chose to skip the scan and fill manually
  const [printing, setPrinting] = useState(false);
  const [pdfing, setPdfing] = useState(false);

  const uiLang = currentLanguage === 'bn' ? 'bn' : 'en';
  const isBnUi = uiLang === 'bn';
  const isBnDoc = docLang === 'bn';

  // ── Load catalog + resume any in-progress draft ────────────────────────────
  useEffect(() => {
    aiDocumentService
      .getTemplates(selectedOfficeSpace?.id ? { officeSpaceId: selectedOfficeSpace.id } : {})
      .then((res) => {
        setCatalog(res.data);
        setCreditCost(res.data?.creditCost ?? null);
      })
      .catch(() => {});
  }, [selectedOfficeSpace?.id]);

  useEffect(() => {
    if (location.pathname.endsWith('/new')) { sessionStorage.removeItem(DRAFT_KEY); return; }
    const savedId = sessionStorage.getItem(DRAFT_KEY);
    if (!savedId) return;
    aiDocumentService
      .get(savedId)
      .then((res) => {
        const d = res.data?.document;
        if (!d || d.status !== 'draft') { sessionStorage.removeItem(DRAFT_KEY); return; }
        setDocId(d.id);
        setDocLang(d.language || 'bn');
        setSelectedType(d.type || null);
        setChatText(d.brief || '');
        if (d.plan?.formPending) {
          // Reloaded while a photographed form was still having its blanks named — finish that now.
          setPlanStage('fields');
          setStep(1);
          aiDocumentService.planFields(d.id)
            .then((r2) => {
              setPlan(r2.data.plan);
              setFields({ ...dateDefaults(r2.data.plan), ...(d.fields && typeof d.fields === 'object' ? d.fields : {}) });
            })
            .catch((e) => { sessionStorage.removeItem(DRAFT_KEY); setDocId(null); setStep(0); if (e?.code === 'INSUFFICIENT_CREDITS') setLimitOpen(true); });
        } else if (d.plan) {
          setPlan(d.plan);
          setFields({ ...dateDefaults(d.plan), ...(d.fields && typeof d.fields === 'object' ? d.fields : {}) });
          setStep(1);
        }
      })
      .catch(() => sessionStorage.removeItem(DRAFT_KEY));
  }, []);

  useEffect(() => {
    if (docId) sessionStorage.setItem(DRAFT_KEY, docId);
  }, [docId]);

  useEffect(() => () => { if (revealTimer.current) clearInterval(revealTimer.current); }, []);

  // The inline editor step is gone: the advanced (PDF) editor opens DIRECTLY, shows its
  // own loading while the document is generated, then loads the built content. The editor
  // is a full-screen overlay, so it covers the (unused) step-3 page behind it.
  const [advLoading, setAdvLoading] = useState(false);
  const layoutBuilt = useRef(false); // set once a reference-image doc has been built into the editor
  const autoFinalizedDoc = useRef(null); // docId that already had its automatic generate (never retried in a loop)
  // 1) Open the editor the moment step 3 begins — on a blank placeholder + loading.
  useEffect(() => {
    if (step !== 2 || advOpen) return undefined;
    let alive = true;
    (async () => {
      const file = await blankDocFile({ paper, landscape }, displayName || docTypeName || 'document');
      if (!alive || layoutBuilt.current) return; // a reference-image doc may already have landed
      setAdvDoc({ file, objects: [] });
      setAdvLoading(true);
      setAdvOpen(true);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  // 2) Once generated, build the document and load it into the already-open editor.
  useEffect(() => {
    const ready = docBlocks.length > 0 || docHtml != null;
    if (!ready || !advLoading) return undefined;
    let alive = true;
    (async () => {
      try {
        const name = displayName || docTypeName || 'document';
        const built = savedLayout
          ? await objectLayoutToPdf(savedLayout, name)
          : await docToObjects({
              name,
              settings: { paper, landscape, globalFontScale, margins, globalFont, isBn: docLang === 'bn' },
              ...(docHtml != null ? { html: docHtml } : { blocks: docBlocks }),
            });
        if (alive) setAdvDoc(built);
      } catch (e) {
        notifications.show({ message: e?.message || t('aiDocuments.advEditFailed', { defaultValue: 'Could not open the editor.' }), color: 'red' });
      } finally {
        if (alive) setAdvLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docBlocks, docHtml, advLoading]);

  // "/" focuses the search box (GitHub/Slack style) — ignored while typing in any field.
  useEffect(() => {
    if (step !== 0) return undefined;
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) return;
      e.preventDefault();
      searchInputRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [step]);

  const TYPES = useMemo(() => (catalog ? getTypes(catalog, uiLang) : []), [catalog, uiLang]);
  const SCHEMAS = useMemo(() => (catalog ? getSchemas(catalog, uiLang) : {}), [catalog, uiLang]);
  const popularChips = useMemo(() => (catalog ? getChips(catalog, uiLang) : []), [catalog, uiLang]);
  // (The old hardcoded preset cards are gone — predefined documents now come from the
  // template-set library below; the AI path is the fallback for unmatched requests.)

  // Ready-made MANUAL templates matching the search — checked FIRST because they cost
  // NOTHING (no AI): the curated /template-sets library already has {placeholder} docs,
  // so picking one routes straight into the manual generate flow (fill → done).
  const [readyTemplates, setReadyTemplates] = useState([]);
  useEffect(() => {
    const q = chatText.trim();
    if (!q || q.length < 2 || sampleImage) { setReadyTemplates([]); return undefined; }
    const tmr = setTimeout(() => {
      // Banglish tolerance: for Latin input, append a phonetic Bangla transliteration —
      // the backend's consonant-skeleton match absorbs the phonetic spelling noise
      // (jomi→জোমি still meets জমি on the skeleton জম).
      let q2 = q;
      if (/[a-zA-Z]/.test(q)) {
        try {
          // English-ish spellings first (the phonetic engine reads 'c' as চ):
          // ck→k, soft c→s, hard c→k — so "pickup"/"license" transliterate usefully.
          const prepped = q.toLowerCase().replace(/ck/g, 'k').replace(/c(?=[eiy])/g, 's').replace(/c/g, 'k');
          const bn = banglishToBangla(prepped);
          if (bn && bn.trim() && bn !== q) q2 = `${q} ${bn}`;
        } catch { /* transliteration is best-effort */ }
      }
      templateSetService.suggestTemplateSets({ q: q2, limit: 4 })
        .then((res) => setReadyTemplates(Array.isArray(res?.data) ? res.data.slice(0, 4) : []))
        .catch(() => setReadyTemplates([]));
    }, 300);
    return () => clearTimeout(tmr);
  }, [chatText, sampleImage]);

  // The predefined fields for the chosen document (NID subset + extra manual fields),
  // resolved for the document language. Drives step 2's form, the background template
  // request, and the final token-replace. Hardcoded in the frontend (aiDocFields.js).
  const docFields = useMemo(() => {
    // AI-planned docs (BOTH preset types and free-text now go through the modular planner) →
    // use the plan's document-specific fields. A NID-sourced field keeps its own id; the scan
    // fills it by label (scanByFills), so no canonical nidField is needed.
    if (plan && (plan.scanByFills || plan.custom) && Array.isArray(plan.fields) && plan.fields.length) {
      return plan.fields.map((f) => ({
        id: f.id,
        type: f.type || 'text',
        label: f.label || f.id,
        source: f.source === 'nid' ? 'nid' : (f.source === 'birth_certificate' ? 'birth_certificate' : 'manual'),
        person: f.person || null,
        nidField: f.nidField || null,
      }));
    }
    if (selectedType) return resolveDocFields(selectedType, docLang);
    return resolveDocFields(null, docLang);
  }, [selectedType, docLang, plan]);
  // Multi-person docs — one NID slot per person; null otherwise. AI-planned docs express parties
  // via multiple per-person sources (party_1_nid…), so their `persons` is empty and this is null.
  const docPersons = useMemo(() => {
    if (plan && (plan.scanByFills || plan.custom)) {
      return (Array.isArray(plan.persons) && plan.persons.length >= 2) ? plan.persons.map((p) => ({ key: p.key, label: p.label })) : null;
    }
    if (selectedType) return getDocPersons(selectedType, docLang);
    return null;
  }, [selectedType, docLang, plan]);
  const isMultiPerson = !!(docPersons && docPersons.length > 1);
  // Free-text custom docs: the fields/parties aren't known until the AI plan lands, so we
  // show a brief "preparing" state rather than a lone generic box that then gets replaced
  // (a confusing one-then-reveal flash). Reference-IMAGE docs (sampleImage set) skip this —
  // they always need an NID upload, so show the upload box INSTANTLY like a preset doc while
  // the (slower vision) plan runs in the background; the scan queues until the plan lands.

  const docTypeName = plan?.name || (selectedType && SCHEMAS[selectedType]?.name) || chatText.trim() || '';
  // Display name honours an inline rename (step 3); falls back to the plan/type name.
  const displayName = (renamedName != null ? renamedName : docTypeName);
  // Commit a step-3 rename: trim, keep a non-empty name, reflect locally + persist to the server.
  const saveDocName = (raw) => {
    setEditingName(false);
    const next = String(raw || '').trim();
    if (!next || next === displayName) return;
    setRenamedName(next);
    if (docId) aiDocumentService.update(docId, { name: next }).catch(() => {});
  };

  // NID/upload sources come ONLY from the plan — never hardcoded. While the plan loads we show
  // the "Analysing…" state (below); no default NID box is assumed for any document.
  const displaySources = (isMultiPerson
    ? docPersons.map((p) => ({ id: `nid_${p.key}`, label: p.label, kind: 'nid', person: p.key }))
    : (plan?.sources || [])
  ).filter((s) => !dismissedSources.has(s.id));
  // Manual fields not tied to any person (dates, amounts, terms…) — shown in a shared
  // section below the per-person cards for multi-person docs.
  // Shared "other details" section: for multi-person docs, the non-person fields; for
  // reference/form docs, the fields not covered by any upload source (manual entry).
  // A dismissed source's fields fall back to the shared "other details" manual section.
  const coveredIds = new Set((plan?.sources || []).filter((s) => !dismissedSources.has(s.id)).flatMap((s) => (Array.isArray(s.fills) ? s.fills : [])));
  const sharedFields = isMultiPerson
    ? docFields.filter((f) => !f.person)
    : (plan ? docFields.filter((f) => !f.person && !coveredIds.has(f.id)) : []);

  // ── Step 1 → 2: move instantly, plan in the background. Optional { type, brief }
  // override lets a result card / custom card proceed directly on click. ──────
  function proceed(opts = {}) {
    if (planning) return;
    const nextType = 'type' in opts ? opts.type : selectedType;
    const nextBrief = 'brief' in opts ? opts.brief : chatText.trim();
    const tplSet = 'templateSet' in opts ? opts.templateSet : null; // { id, htmlTemplate } — ZERO-AI plan
    if (!nextType && !nextBrief && !sampleImage && !tplSet) {
      notifications.show({ message: t('aiDocuments.pickOrDescribe', { defaultValue: 'Pick a document type, describe what you need, or upload a sample.' }), color: 'yellow' });
      return;
    }
    if (!isAdmin && !selectedOfficeSpace?.id) {
      notifications.show({ message: t('aiDocuments.noOfficeSpace', { defaultValue: 'Please select an office space first.' }), color: 'red' });
      return;
    }
    // Reflect an explicit choice in state so step 2 (fields, header, template) reads it.
    if ('type' in opts) setSelectedType(opts.type);
    if ('brief' in opts) setChatText(opts.brief);
    setRenamedName(null); // a NEW document must not inherit the previous doc's renamed title
    // Template-set picks carry the source's exact page margins + size; other flows reset
    // to the default preset so a prior template's margins never leak into an AI doc.
    setSourceMargins(opts.sourceMargins || null);
    if (opts.sourcePaper) setPaper(opts.sourcePaper);
    setPlan(null);
    setFields({});
    setCollected(new Set());
    setSourceImages({});
    setDismissedSources(new Set());
    setExtractedBySource({});
    setRevealCount({});
    pendingScanRef.current = null;
    setDocBlocks([]);
    setDocHtml(null);
    setTemplateBlocks(null);
    setTemplateHtml(null);
    setTemplateLoading(false);
    setHasGenerated(false);
    setSkipScan(false);
    const seq = ++planSeq.current; // this plan's ticket — startOver()/a newer proceed() invalidates it
    setPlanStage('read');
    setPlanning(true);
    setStep(1);
    window.scrollTo({ top: 0 });

    const typeName = nextType ? SCHEMAS[nextType]?.name || '' : '';
    const replanId = docId && !hasGenerated ? docId : null;
    aiDocumentService
      .plan({
        docId: replanId || undefined,
        officeSpaceId: selectedOfficeSpace?.id,
        type: nextType || '',
        typeName,
        brief: nextBrief,
        language: docLang,
        referenceImage: tplSet ? undefined : (sampleImage || undefined),
        templateSetId: tplSet ? tplSet.id : undefined,
        htmlTemplate: tplSet ? tplSet.htmlTemplate : undefined,
      })
      .then((res) => {
        if (planSeq.current !== seq) return; // superseded (user started over / re-proceeded)
        setDocId(res.data.docId);
        autoFinalizedDoc.current = null; // a fresh plan earns a fresh automatic generate
        if (res.data.plan?.formPending) {
          // The photo is a FORM: its blanks still have to be named. Keep the progress screen up, now
          // talking about fields (a slip or letter never gets here — it is complete already).
          setPlanStage('fields');
          return aiDocumentService.planFields(res.data.docId).then((r2) => {
            if (planSeq.current !== seq) return;
            setPlan(r2.data.plan);
            setFields((p) => ({ ...dateDefaults(r2.data.plan), ...p }));
          });
        }
        setPlan(res.data.plan);
        setFields((p) => ({ ...dateDefaults(res.data.plan), ...p }));
        return undefined;
      })
      .catch((e) => {
        if (planSeq.current !== seq) return;
        // The balance can't cover the document → nothing was started; show the top-up dialog, not a raw error.
        if (e?.code === 'INSUFFICIENT_CREDITS') setLimitOpen(true);
        else notifications.show({ message: e.message || t('aiDocuments.planFailed', { defaultValue: 'Planning failed.' }), color: 'red' });
        setStep(0);
      })
      .finally(() => { if (planSeq.current === seq) setPlanning(false); });
  }
  // Consonant-skeleton of a Bangla string (drop vowel signs + independent vowels), so
  // spelling variants collapse — used to auto-match a typed name to a template name.
  const docSkel = (s) => String(s || '').normalize('NFC').toLowerCase()
    .split('').filter((c) => !'ািীুূৃেৈোৌ্ঁঃ়অআইঈউঊঋএঐওঔ'.includes(c)).join('');
  // Strong template match: ≥70% of the typed name's words appear in a template's name
  // (query Bangla-normalised first so "muslim nikah nama" meets "মুসলিম নিকাহ নামা").
  const bestTemplateMatch = (q, list) => {
    if (!q || !Array.isArray(list) || !list.length) return null;
    let qb = q;
    if (/[a-z]/i.test(q)) { try { qb = banglishToBangla(q.toLowerCase().replace(/ck/g, 'k').replace(/c(?=[eiy])/g, 's').replace(/c/g, 'k')); } catch { qb = q; } }
    const qWords = qb.split(/[\s,-]+/).map(docSkel).filter((w) => w.length >= 2);
    if (!qWords.length) return null;
    for (const t of list) {
      const nameSkel = String(t.name || '').split(/[\s,-]+/).map(docSkel).join(' ');
      const hit = qWords.filter((w) => nameSkel.includes(w)).length;
      if (hit >= Math.ceil(qWords.length * 0.7)) return t;
    }
    return null;
  };

  // Result-card / custom-card click handlers — proceed straight to step 2. "Next" auto-uses
  // a strongly-matching template (free, curated) before falling back to the AI planner.
  const proceedCustom = () => {
    const q = chatText.trim();
    const best = bestTemplateMatch(q, readyTemplates);
    if (best) { pickReadyTemplate(best); return; }
    proceed({ type: null, brief: q });
  };

  // Ready-made template pick — ZERO AI. Load the set's Lexical docs from R2, convert to
  // HTML client-side (same pipeline the manual preview uses), join multi-doc bundles with
  // an explicit page break, and plan via the template-set branch (fields = the set's
  // curated placeholders; only the NID scan costs anything).
  const [pickingSetId, setPickingSetId] = useState(null);
  async function pickReadyTemplate(ts) {
    if (planning || pickingSetId) return;
    setPickingSetId(ts.id);
    try {
      const res = await templateSetService.getTemplateSetById(ts.slug ?? ts.id);
      const set = res?.data;
      const docs = [...(set?.docs || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
      const R2 = import.meta.env.VITE_R2_PUBLIC_URL || '';
      const parts = [];
      for (const d of docs) {
        let json = null;
        if (d.contentKey) {
          try {
            const r = await fetch(`${R2}/${d.contentKey}`);
            if (r.ok) json = await r.json();
          } catch { json = null; }
        }
        if (!json && d.content) { try { json = JSON.parse(d.content); } catch { json = null; } }
        if (json) {
          const h = lexicalToHtml(json);
          if (h && h.trim()) parts.push(h);
        }
      }
      if (!parts.length) throw new Error('empty template');
      const htmlTemplate = parts.join('<div class="adoc-break"></div>');
      // Carry the source doc's OWN page size + margins so the generated document matches
      // the template exactly (the manual editor stores px margins in each doc's metadata,
      // falling back to a named preset). Uses the first doc's settings for the whole bundle.
      const PAGE_MARGIN_PRESETS = {
        narrow: { top: 48, bottom: 48, left: 36, right: 36 },
        normal: { top: 72, bottom: 72, left: 72, right: 72 },
        moderate: { top: 60, bottom: 60, left: 54, right: 54 },
        wide: { top: 96, bottom: 96, left: 96, right: 96 },
        stamp: { top: 216, bottom: 72, left: 18, right: 18 },
      };
      const md0 = docs[0]?.metadata || {};
      const lm = PAGE_MARGIN_PRESETS[String(md0.margin || 'normal').toLowerCase()] || PAGE_MARGIN_PRESETS.normal;
      const srcMargins = {
        top: typeof md0.marginTop === 'number' ? md0.marginTop : lm.top,
        bottom: typeof md0.marginBottom === 'number' ? md0.marginBottom : lm.bottom,
        left: typeof md0.marginLeft === 'number' ? md0.marginLeft : lm.left,
        right: typeof md0.marginRight === 'number' ? md0.marginRight : lm.right,
      };
      const sizeKey = String(md0.pageSize || 'A4').toLowerCase();
      const srcPaper = ['a4', 'letter', 'legal', 'a5'].includes(sizeKey) ? sizeKey : 'a4';
      proceed({ templateSet: { id: set.id, htmlTemplate }, type: null, brief: chatText.trim(), sourceMargins: srcMargins, sourcePaper: srcPaper });
    } catch {
      notifications.show({ message: t('aiDocuments.readyTemplateFailed', { defaultValue: 'Could not load that template. Please try again.' }), color: 'red' });
    } finally {
      setPickingSetId(null);
    }
  }

  // Quick-start badges. 'text' focuses the describe box; every other mode opens the image
  // picker (restore / question-paper carry a framing note the planner reads as the brief).
  const onModeBadge = (mode, brief = '') => {
    if (mode === 'text') { searchInputRef.current?.focus(); return; }
    badgeBrief.current = brief || '';
    badgeFileRef.current?.click();
  };

  // Optional: attach a sample/reference of the document the user wants. The image is
  // sent to plan() as `referenceImage` so the AI can mirror its structure.
  async function onSampleFile(file) {
    if (!file) return;
    setSampleBusy(true);
    // Open the SAME crop dialog as the document-scan upload (free crop) so the user can trim the
    // reference to just the document first. The dialog encodes the crop at ≤2000px JPEG, so
    // onCropConfirm can set it as the sample directly — no separate downscale needed.
    setCropState({ open: true, imageSrc: null, initialCrop: null, sourceId: null, person: null, fillFields: null, targetFields: null, forSample: true });
    try {
      const raw = isPdf(file) ? (await pdfToImages(file))[0] : await fileToDataUrl(file);
      if (!raw) throw new Error('read failed');
      setCropState((s) => (s.open && s.forSample ? { ...s, imageSrc: raw } : s));
    } catch {
      setCropState({ open: false, imageSrc: null, initialCrop: null, sourceId: null, person: null, fillFields: null, targetFields: null, forSample: false });
      notifications.show({ message: t('aiDocuments.uploadFailed', { defaultValue: 'Could not read that file.' }), color: 'red' });
    } finally {
      setSampleBusy(false);
    }
  }

  // Step-1 shortcuts: Ctrl/Cmd+V pastes a copied image as the sample, Ctrl/Cmd+U opens the
  // file picker (same dialog as the + button). Only while no sample is attached and nothing is
  // busy, so they never fight the later steps.
  useEffect(() => {
    if (step !== 0 || sampleImage || planning || sampleBusy || cropState.open) return undefined;
    const onPaste = (e) => {
      const item = Array.from(e.clipboardData?.items || []).find((i) => i.kind === 'file' && i.type.startsWith('image/'));
      const file = item?.getAsFile();
      if (!file) return; // plain text paste goes to the input as usual
      e.preventDefault();
      badgeBrief.current = '';
      onSampleFile(file);
    };
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'u') {
        e.preventDefault(); // browsers bind Ctrl+U to "view source"
        badgeBrief.current = '';
        badgeFileRef.current?.click();
      }
    };
    window.addEventListener('paste', onPaste);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('paste', onPaste); window.removeEventListener('keydown', onKey); };
  }, [step, sampleImage, planning, sampleBusy, cropState.open]);

  // A scan done before the plan arrived is re-targeted to the plan's first source.
  useEffect(() => {
    if (!plan || !docId || !pendingScanRef.current) return;
    const img = pendingScanRef.current;
    pendingScanRef.current = null;
    const target = (plan.sources || [])[0];
    setSourceImages((p) => {
      const q = { ...p };
      delete q[PENDING_SRC_ID];
      if (target) q[target.id] = img;
      return q;
    });
    if (target) actualExtract(target.id, img);
    else setExtracting(null);
  }, [plan, docId]);

  // ── Step 2: pre-generate the {token} template in the background (during the scan) ──
  // Once the draft + plan exist, ask the server for the cached tokenised template for
  // this doc type + language (generated once, reused for everyone). The user is busy
  // scanning/typing, so the AI latency is hidden and "Generate" becomes an instant,
  // no-AI token-replace. If this fails, finalize() falls back to server-side generation.
  useEffect(() => {
    if (step !== 1 || !docId || !plan) return;
    if (templateBlocks || templateHtml || templateLoading) return;
    if (plan.format === 'layout') return; // layout docs have no AI template to prefetch
    if (!docFields.length) return;
    setTemplateLoading(true);
    aiDocumentService
      .template({ docId, fields: docFields.map((f) => ({ id: f.id, label: f.label, type: f.type })) })
      .then((res) => {
        if (res.data?.html) setTemplateHtml(res.data.html);
        else setTemplateBlocks(res.data?.blocks || []);
      })
      .catch(() => {})
      .finally(() => setTemplateLoading(false));
  }, [step, docId, plan, docFields, templateBlocks, templateHtml, templateLoading]);

  // ── Step 2: upload → on-page scanning animation → tap-tap reveal ───────────
  async function onSourceFiles(fileList, source) {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    const f = list[0];
    // Targeted OCR (read the source's OWN fields by label) when: a non-NID doc, OR an AI-planned
    // doc (scanByFills) whose NID fills are document-specific ids (full_name, party_1_name…) that
    // a canonical NID scan can't map. A canonical NID doc (reference flow) still does a full scan.
    const targetFields = (Array.isArray(source.fills) && source.fills.length && ((source.kind && source.kind !== 'nid') || plan?.scanByFills))
      ? docFields.filter((fd) => source.fills.includes(fd.id)).map((fd) => ({ id: fd.id, label: fd.label }))
      : null;
    const closeCrop = () => setCropState({ open: false, imageSrc: null, initialCrop: null, sourceId: null, person: null, fillFields: null, targetFields: null });
    // Open the crop dialog right away in a loading state — NID card auto-detection can
    // take a moment on a large photo, and the dialog shows a spinner until the image is ready.
    setCropState({ open: true, imageSrc: null, initialCrop: null, sourceId: source.id, person: source.person || null, fillFields: source.fillFields || null, targetFields });
    try {
      let rawSrc;
      let initialCrop = null; // auto-detected card box (adjustable) — we suggest, never hard-crop
      if (isPdf(f)) {
        const pages = await pdfToImages(f);
        if (!pages.length) {
          closeCrop();
          notifications.show({ message: t('aiDocuments.pdfFailed', { defaultValue: 'Could not read that PDF.' }), color: 'red' });
          return;
        }
        rawSrc = pages[0];
      } else if (f.type?.startsWith('image/')) {
        rawSrc = await fileToDataUrl(f);
        // NID uploads: auto-DETECT the card and pre-position the crop box on the FULL
        // image (never auto-crop) so the user can adjust it. "Fill" scans + PDFs skip this.
        if (!source.fillFields) {
          try {
            const { detectCardQuadFromImage, MIN_AUTO_CROP_CONFIDENCE } = await import('../tools/image/nid-to-pdf/detectCardQuad');
            const el = await loadImageEl(rawSrc);
            const det = detectCardQuadFromImage(el);
            if (det?.quad && det.confidence >= MIN_AUTO_CROP_CONFIDENCE) {
              initialCrop = quadToPercentCrop(det.quad, el.naturalWidth, el.naturalHeight);
            }
          } catch { /* detection failed → no suggested box, full-image crop */ }
        }
      } else {
        closeCrop();
        return;
      }
      // Only apply if the dialog is still open (the user may have cancelled meanwhile).
      setCropState((s) => (s.open && s.sourceId === source.id ? { ...s, imageSrc: rawSrc, initialCrop } : s));
    } catch {
      closeCrop();
      notifications.show({ message: t('aiDocuments.uploadFailed', { defaultValue: 'Could not read that file.' }), color: 'red' });
    }
  }

  // Crop confirmed → close the modal immediately; the scan runs here on the page
  // with a scanning animation over the document.
  function onCropConfirm(image) {
    if (!image) return; // ignore a confirm fired before the image finished loading/detecting
    const forSample = cropState.forSample || false;
    const sid = cropState.sourceId;
    const person = cropState.person || null;
    const fillFields = cropState.fillFields || null;
    const targetFields = cropState.targetFields || null;
    setCropState({ open: false, imageSrc: null, initialCrop: null, sourceId: null, person: null, fillFields: null, targetFields: null, forSample: false });
    // Reference/sample image cropped → set it as the sample (the dialog already encoded ≤2000px JPEG).
    if (forSample) { setSampleImage(image); return; }
    // "Fill from a document" — scan a supporting doc for a set of fields (no image card).
    if (fillFields) { actualExtractInto(image, fillFields); return; }
    setSourceImages((p) => ({ ...p, [sid]: image }));
    setExtractedBySource((p) => ({ ...p, [sid]: [] }));
    setRevealCount((p) => ({ ...p, [sid]: 0 }));
    if (sid === PENDING_SRC_ID && !plan) {
      // Plan still loading — hold the image; the effect above runs it when ready.
      pendingScanRef.current = image;
      setExtracting(PENDING_SRC_ID);
      return;
    }
    actualExtract(sid, image, person, targetFields);
  }

  async function actualExtract(sid, image, person = null, targetFields = null) {
    if (!docId) {
      notifications.show({ message: t('aiDocuments.planningWait', { defaultValue: 'Preparing… please try again in a moment.' }), color: 'yellow' });
      return;
    }
    setExtracting(sid);
    try {
      // Non-NID documents read their OWN fields (targeted OCR); the NID does a full scan.
      const res = await aiDocumentService.extract({ docId, sourceId: sid, image, targetFields: (targetFields && targetFields.length) ? targetFields : undefined });
      const raw = res.data?.values || {};
      // For a multi-person slot, route the generic NID values onto THAT person's
      // prefixed fields (groom_name_bn, …) so two people never overwrite each other.
      let values = raw;
      if (person) {
        // A combined "ঠিকানা" field (nidField 'address') is joined from the NID's
        // village/post/upazila/district; other fields map straight from the canonical id.
        const addr = ['village', 'post_office', 'upazila', 'district']
          .map((k) => (raw[k] != null ? String(raw[k]).trim() : '')).filter(Boolean).join(', ');
        values = {};
        for (const fd of docFields) {
          if (fd.person !== person || !fd.nidField) continue;
          const v = fd.nidField === 'address' ? addr : raw[fd.nidField];
          if (v != null && String(v).trim()) values[fd.id] = v;
        }
      } else if (!targetFields || !targetFields.length) {
        // Single-applicant NID scan: canonical values already land on any canonical-id
        // fields; ALSO fill the form's own manual name/parent/DOB/NID/address fields by
        // matching their labels, so custom-id forms (e.g. multi-role appraisal) get filled.
        values = { ...raw, ...mapNidByLabel(raw, docFields, fields) };
      }
      // Pre-fill the obvious DERIVED fields (nationality → বাংলাদেশী, religion inferred from the
      // scanned name) so the user types less — only empties, scoped to the scanned person.
      const scannedName = raw.name_bn || raw.name_en || '';
      Object.assign(values, smartDefaults(docFields, { ...fields, ...values }, scannedName, person, docLang === 'bn'));
      const keys = Object.keys(values);
      if (!keys.length) {
        notifications.show({ message: t('aiDocuments.scanNone', { defaultValue: 'No fields could be read — try a clearer scan.' }), color: 'yellow' });
        setExtractedBySource((p) => ({ ...p, [sid]: [] }));
      } else {
        setFields((p) => ({ ...p, ...values }));
        setExtractedBySource((p) => ({ ...p, [sid]: keys }));
        setCollected((prev) => {
          const s = new Set(prev);
          keys.forEach((k) => s.add(k));
          return s;
        });
        // "Tap-tap" staggered reveal of the extracted rows.
        if (revealTimer.current) clearInterval(revealTimer.current);
        let i = 0;
        setRevealCount((p) => ({ ...p, [sid]: 0 }));
        revealTimer.current = setInterval(() => {
          i += 1;
          setRevealCount((p) => ({ ...p, [sid]: i }));
          if (i >= keys.length) { clearInterval(revealTimer.current); revealTimer.current = null; }
        }, 320);
      }
    } catch (e) {
      if (e?.code === 'INSUFFICIENT_CREDITS') setLimitOpen(true); // the balance no longer covers the document
      else notifications.show({ message: e.message || t('aiDocuments.scanFailed', { defaultValue: 'Scan failed.' }), color: 'red' });
    } finally {
      setExtracting(null);
    }
  }

  // Scan a SUPPORTING document (not an NID) and auto-fill the given fields from it —
  // only fills fields that are still EMPTY (never overwrites what the user/scan set).
  async function actualExtractInto(image, targetDefs) {
    if (!docId || !Array.isArray(targetDefs) || !targetDefs.length) return;
    setFillBusy(true);
    setFillPreview(image);
    try {
      const res = await aiDocumentService.extract({ docId, image, targetFields: targetDefs.map((f) => ({ id: f.id, label: f.label })) });
      const values = res.data?.values || {};
      const filled = Object.entries(values).filter(([k, v]) => String(v ?? '').trim() && !String(fields[k] ?? '').trim());
      if (filled.length) {
        setFields((p) => { const next = { ...p }; filled.forEach(([k, v]) => { if (!String(next[k] ?? '').trim()) next[k] = v; }); return next; });
        notifications.show({ message: t('aiDocuments.filledFromDoc', { defaultValue: 'Filled the matching fields from your document.' }), color: 'green' });
      } else {
        notifications.show({ message: t('aiDocuments.fillNone', { defaultValue: 'Could not match any field from that document.' }), color: 'yellow' });
      }
    } catch (e) {
      if (e?.code === 'INSUFFICIENT_CREDITS') setLimitOpen(true); // the balance no longer covers the document
      else notifications.show({ message: e.message || t('aiDocuments.scanFailed', { defaultValue: 'Scan failed.' }), color: 'red' });
    } finally {
      setFillBusy(false);
      setFillPreview(null);
    }
  }

  const setField = (id, v) => setFields((p) => ({ ...p, [id]: v }));

  // ── Finalize (instant, no-AI) / regenerate ─────────────────────────────────
  // "Generate" swaps the pre-built {token} template with the collected values, so
  // the finished document appears in seconds — no waiting on AI. "Regenerate"
  // (isRegen) forces a fresh AI template on the server first, then swaps. Both
  // deduct one credit (charged on finalize).
  async function runFinalize(isRegen) {
    if (!docId) return;
    if (isRegen && manualEditing) return;
    layoutBuilt.current = false;
    setGenerating(true);
    // First generation: jump to step 3 immediately.
    if (!isRegen) {
      setDocBlocks([]);
      setDocHtml(null);
      setManualEditing(false);
      setStep(2);
      window.scrollTo({ top: 0 });
    }

    // Normalised values + labeled field list for the doc's predefined fields.
    const values = {};
    docFields.forEach((f) => {
      let v = fields[f.id] || '';
      if (f.type === 'date' && v) v = formatDateValue(v);
      values[f.id] = v;
    });
    const fieldList = docFields.map((f) => ({ id: f.id, label: f.label, type: f.type, value: values[f.id] }));

    // Instant path: if the template is ready, show the filled document immediately
    // from the local replace while the server persists + charges in the background.
    if (!isRegen && templateBlocks && templateBlocks.length) {
      setDocBlocks(applyTemplate(templateBlocks, docFields, fields, docLang));
    } else if (!isRegen && templateHtml) {
      setDocHtml(sanitizeDocHtml(applyTemplateHtml(templateHtml, docFields, fields, docLang), { preserveStyles: !!plan?.preserveStyles }));
    }

    try {
      const res = await aiDocumentService.finalize({
        docId,
        fields: values,
        fieldList,
        // Reuse the template already fetched in the background so finalize is instant
        // (and custom docs aren't re-generated); regenerate forces a fresh one.
        templateBlocks: (!isRegen && templateBlocks && templateBlocks.length) ? templateBlocks : undefined,
        templateHtml: (!isRegen && templateHtml) ? templateHtml : undefined,
        refreshTemplate: !!isRegen,
      });
      if (res.data?.layout) {
        // Reference-image document (the OCR text) → straight into the SAME document editor as
        // every other AI document — nothing is built as a PDF server-side.
        const built = await layoutToPdf(res.data.layout, { sampleImage, name: res.data.name || docTypeName || 'document' });
        layoutBuilt.current = true;
        setSavedLayout(null);
        setAdvDoc(built);
        setAdvLoading(false);
        setAdvOpen(true);
        setHasGenerated(true);
        return;
      } else if (res.data?.html != null) {
        // HTML-format (form/table) document — trust the server's localized HTML.
        setDocHtml(sanitizeDocHtml(res.data.html, { preserveStyles: !!plan?.preserveStyles }));
      } else {
        let blocks = res.data?.blocks || [];
        if (docLang === 'bn') blocks = blocks.map((b) => ({ ...b, text: toBanglaDigits(b.text) }));
        if (blocks.length) setDocBlocks(blocks);
      }
      setHasGenerated(true);
      if (isRegen) {
        setTemplateBlocks(null); // a fresh template was created — drop the local copy
        setTemplateHtml(null);
        notifications.show({ message: t('aiDocuments.regenerated', { defaultValue: 'Document regenerated.' }), color: 'green' });
      }
    } catch (e) {
      if (e?.code === 'INSUFFICIENT_CREDITS') setLimitOpen(true);
      else notifications.show({ message: e.message || t('aiDocuments.genFailed', { defaultValue: 'Generation failed.' }), color: 'red' });
      if (!isRegen) {
        // failed — back to details. The editor placeholder opened with step 3 must go too, or its
        // loader stays on screen for ever.
        setDocBlocks([]); setDocHtml(null);
        setAdvOpen(false); setAdvDoc(null); setAdvLoading(false);
        setStep(1);
      }
    } finally {
      setGenerating(false);
    }
  }

  // A document with NOTHING to fill (pure content reproduction — a poem, notice, book
  // page: zero fields AND zero upload sources) has no reason to show step 2. Generate
  // immediately and land on the final preview.
  useEffect(() => {
    if (!plan || !docId || step !== 1 || hasGenerated || generating) return;
    if (plan.format !== 'layout' && (typeof plan.htmlTemplate !== 'string' || !plan.htmlTemplate)) return; // html / layout docs only
    const noFields = !(Array.isArray(plan.fields) && plan.fields.length);
    const noSources = !(Array.isArray(plan.sources) && plan.sources.length);
    // Once per document: if it fails the user lands back here, and without this guard the effect
    // would fire again at once — an endless loop of failing (and charge-then-refund) requests.
    if (noFields && noSources && autoFinalizedDoc.current !== docId) {
      autoFinalizedDoc.current = docId;
      runFinalize(false);
    }
  }, [plan, docId, step, hasGenerated, generating]);

  // ── Step 3: inline edits (debounced persist). `text` may be undefined when only
  // styling metadata (align / font) changed. ─────────────────────────────────
  function onBlockEdit(idx, text, meta) {
    const snapshot = docBlocks.map((b, i) =>
      i === idx ? { ...b, ...(text !== undefined ? { text } : {}), ...(meta || {}) } : b,
    );
    setDocBlocks(snapshot);
    setEdited(true);
    if (text !== undefined) setManualEditing(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      aiDocumentService.update(docId, { blocks: snapshot }).catch(() => {});
    }, 900);
  }

  // HTML-format inline edit (whole-document contentEditable) — debounced persist.
  function onHtmlEdit(nextHtml) {
    setDocHtml(nextHtml);
    setEdited(true);
    setManualEditing(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      aiDocumentService.update(docId, { html: nextHtml }).catch(() => {});
    }, 900);
  }

  // Advanced edit — flatten the current block/html document (honouring the page/font/
  // margin settings) into the shared PDF editor for precise positioning. Saving there
  // persists the editor's objects as a `layout` block (reusing the reference-image path),
  // so the document reopens straight into the editor.
  async function openAdvancedEdit() {
    if (advBuilding) return;
    const hasContent = docBlocks.length > 0 || docHtml != null;
    if (!hasContent) return;
    // Flush any pending debounced inline edit so the editor opens on the latest content.
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    setAdvBuilding(true);
    try {
      const name = displayName || docTypeName || 'document';
      // Reopen the previously saved PDF-edit if there is one; otherwise flatten the
      // current block/html content with the active page settings.
      const built = savedLayout
        ? await objectLayoutToPdf(savedLayout, name)
        : await docToObjects({
            name,
            settings: { paper, landscape, globalFontScale, margins, globalFont, isBn: docLang === 'bn' },
            ...(docHtml != null ? { html: docHtml } : { blocks: docBlocks }),
          });
      setAdvDoc(built);
      setAdvOpen(true);
    } catch (e) {
      notifications.show({ message: e?.message || t('aiDocuments.advEditFailed', { defaultValue: 'Could not open the editor.' }), color: 'red' });
    } finally {
      setAdvBuilding(false);
    }
  }

  // The PDF editor finished: persist its objects as the document's layout, then hand
  // the user the edited PDF. Errors in persistence never block the download.
  async function onAdvancedDone(blob, fname) {
    try {
      setAdvSaving(true);
      const st = usePdfEditorStore.getState();
      const layout = serializeEditorLayout(st.pages, st.objectsByUid);
      if (layout.objects.length) {
        if (docId) await aiDocumentService.update(docId, { layout });
        setSavedLayout(layout); // reopen shows the edited version, not the original flatten
      }
      setEdited(false);
    } catch (e) {
      notifications.show({ message: e?.message || t('aiDocuments.advSaveFailed', { defaultValue: 'Saved the file, but could not update the document.' }), color: 'red' });
    } finally {
      setAdvSaving(false);
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = /\.pdf$/i.test(fname || '') ? fname : `${fname || 'document'}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    // Done editing → straight to a fresh document (the layout is already persisted).
    setAdvOpen(false);
    setAdvDoc(null);
    setSavedLayout(null);
    setAdvLoading(false);
    startOver();
  }

  // Close (the only way to finish in document mode now Done is hidden): persist the
  // current edit as the document's layout, then start a fresh document. The editor
  // already asked the user to confirm the exit.
  function onAdvancedClose() {
    // Snapshot first: the editor's store is reset the moment the modal unmounts.
    const id = docId;
    let layout = null;
    if (!advLoading && id) {
      try {
        const st = usePdfEditorStore.getState();
        const snap = serializeEditorLayout(st.pages, st.objectsByUid);
        if (snap.objects.length) layout = snap;
      } catch { /* nothing to save */ }
    }
    // Close at once, save in the background — the button must never wait on the network.
    setAdvOpen(false);
    setAdvDoc(null);
    setSavedLayout(null);
    setAdvLoading(false);
    startOver();
    if (layout) {
      aiDocumentService.update(id, { layout }).catch((e) => {
        notifications.show({ message: e?.message || t('aiDocuments.advSaveFailed', { defaultValue: 'Could not save the document.' }), color: 'red' });
      });
    }
  }

  // Finish: flush any pending inline edits, then return to step 1 to create another
  // document. The document is already persisted at finalize time, so this always
  // works even with no manual edits.
  async function finishAndNew() {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    try {
      if (edited && docId) {
        const payload = docHtml != null
          ? { html: editorRef.current?.getHtml?.() ?? docHtml }
          : { blocks: docBlocks };
        await aiDocumentService.update(docId, payload);
      }
      notifications.show({ message: t('aiDocuments.finished', { defaultValue: 'Document saved.' }), color: 'green' });
    } catch (e) {
      notifications.show({ message: e.message || t('aiDocuments.saveFailed', { defaultValue: 'Save failed.' }), color: 'red' });
      return; // keep the user on the document so they can retry
    }
    startOver();
  }

  async function copyText() {
    try {
      const text = docHtml != null
        ? htmlToText(editorRef.current?.getHtml?.() ?? docHtml)
        : blocksToText(docBlocks);
      await navigator.clipboard.writeText(text);
      notifications.show({ message: t('aiDocuments.copied', { defaultValue: 'Copied to clipboard.' }), color: 'green' });
    } catch {
      notifications.show({ message: t('aiDocuments.copyFailed', { defaultValue: 'Copy failed.' }), color: 'red' });
    }
  }

  const exportOpts = () => ({ isBn: docLang === 'bn', paper, landscape, globalFont, fontScale: globalFontScale, margins, preserveStyles: !!plan?.preserveStyles });
  async function handlePrint() {
    if (printing || (!docBlocks.length && docHtml == null)) return;
    setPrinting(true);
    try {
      if (docHtml != null) await openPrint([], { ...exportOpts(), bodyHtml: editorRef.current?.getHtml?.() ?? docHtml });
      else await openPrint(docBlocks, exportOpts());
    }
    catch { notifications.show({ message: t('aiDocuments.printFailed', { defaultValue: 'Could not open print.' }), color: 'red' }); }
    finally { setPrinting(false); }
  }
  async function handleDownloadPdf() {
    if (pdfing || (!docBlocks.length && docHtml == null)) return;
    setPdfing(true);
    try {
      const opts = { filename: plan?.name || docTypeName || 'document', ...exportOpts() };
      if (docHtml != null) await downloadDocPdf([], { ...opts, bodyHtml: editorRef.current?.getHtml?.() ?? docHtml });
      else await downloadDocPdf(docBlocks, opts);
    }
    catch { notifications.show({ message: t('aiDocuments.pdfFailed2', { defaultValue: 'Could not create the PDF.' }), color: 'red' }); }
    finally { setPdfing(false); }
  }

  function startOver() {
    sessionStorage.removeItem(DRAFT_KEY);
    planSeq.current++; // discard any in-flight plan so its result can't land on the fresh screen
    setPlanning(false); // never carry a stale "planning" spinner onto the new (step-0) screen
    setSampleBusy(false);
    setSkipScan(false);
    setDocId(null);
    setPlan(null);
    setFields({});
    setCollected(new Set());
    setSourceImages({});
    setDismissedSources(new Set());
    setExtractedBySource({});
    setRevealCount({});
    pendingScanRef.current = null;
    setDocBlocks([]);
    setDocHtml(null);
    setTemplateBlocks(null);
    setTemplateHtml(null);
    setTemplateLoading(false);
    setSelectedType(null);
    setChatText('');
    setSampleImage(null);
    setRenamedName(null);
    setSourceMargins(null);
    setReadyTemplates([]);
    setHasGenerated(false);
    setManualEditing(false);
    setEdited(false);
    setStep(0);
  }

  const costLabel = creditCost != null ? (isBnUi ? toBanglaDigits(creditCost) : creditCost) : null;
  const scannedAny = Object.values(extractedBySource).some((a) => (a || []).length > 0);

  // Generate stays locked until a document is uploaded AND its scan has finished
  // (unless the plan needs no upload at all).
  const scanBusy = extracting != null;
  // Scan is OPTIONAL: a reference-image doc (sampleImage) or an explicit "skip" never blocks
  // generating — the NID upload stays available only to auto-fill identity when the user wants.
  const needsUpload = displaySources.length > 0 && !sampleImage && !skipScan;
  const anyUploaded = Object.keys(sourceImages).length > 0;
  const genDisabled = !plan || planning || generating || scanBusy || (needsUpload && !anyUploaded);

  return (
    <div className="w-full py-4 md:py-6">
      <style>{`@keyframes adocAiGlow{0%,100%{opacity:.35}50%{opacity:.8}}@keyframes adocRise{from{opacity:0;transform:translateY(38px)}to{opacity:1;transform:translateY(0)}}@keyframes adocSlideL{from{opacity:0;transform:translateX(-28px)}to{opacity:1;transform:translateX(0)}}@keyframes adocScan{0%{top:-8%}50%{top:100%}100%{top:-8%}}@keyframes adocPulseRing{0%{transform:scale(.85);opacity:.55}70%{opacity:0}100%{transform:scale(1.9);opacity:0}}@keyframes adocIconBob{0%,100%{transform:scale(1)}50%{transform:scale(1.08)}}@keyframes adocBeam{from{top:0%}to{top:100%}}@keyframes adocIndet{0%{transform:translateX(-110%)}100%{transform:translateX(260%)}}@keyframes adocFieldIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}@keyframes adocPanelIn{from{opacity:0;transform:translateX(28px)}to{opacity:1;transform:none}}`}</style>
      {/* Header — the doc-type icon + (double-click to rename) title */}
      {step === 2 && (
        <div className="flex items-center gap-4 flex-wrap">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 shrink-0">
            <DocTypeIcon typeId={selectedType} size={30} />
          </span>
          <div className="min-w-0">
            {editingName ? (
              <input
                autoFocus
                defaultValue={displayName}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); saveDocName(e.currentTarget.value); }
                  else if (e.key === 'Escape') { e.preventDefault(); setEditingName(false); }
                }}
                onBlur={(e) => saveDocName(e.currentTarget.value)}
                className="w-full max-w-xl px-2 py-1 -ml-2 text-xl md:text-2xl font-semibold text-zinc-900 dark:text-zinc-100 bg-white dark:bg-zinc-800 border border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            ) : (
              <h2
                onDoubleClick={() => setEditingName(true)}
                title={t('aiDocuments.renameHint', { defaultValue: 'Double-click to rename' })}
                className="text-xl md:text-2xl font-semibold text-zinc-900 dark:text-zinc-100 cursor-text truncate"
              >
                {displayName || t('aiDocuments.title', { defaultValue: 'AI Document' })}
              </h2>
            )}
            <p className="hidden sm:block text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
              {t('aiDocuments.subtitle', { defaultValue: 'Draft a legal document with AI.' })}
            </p>
          </div>
        </div>
      )}
      <div className="mt-4" />

      {/* ── Step 1: Describe — search-first: results as big cards, else a custom-doc card ── */}
      {step === 0 && (
        // Hero centers the search box before typing, then glides it to the top as results
        // appear — a padding-top transition (not a class swap) keeps that motion smooth.
        <div
          className="max-w-2xl mx-auto transition-[padding] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
          style={{ paddingTop: chatText.trim() ? '1.5rem' : '24vh' }}
        >
          {/* Empty hero — only before the user types */}
          {!chatText.trim() && (
            <div className="text-center mb-6">
              <h3 className="text-xl md:text-2xl font-semibold text-zinc-800 dark:text-zinc-100">
                {t('aiDocuments.searchEmptyTitle', { defaultValue: 'What document do you need?' })}
              </h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1.5 max-w-md mx-auto">
                {t('aiDocuments.searchEmptyHint', { defaultValue: 'Describe the document you need in detail below.' })}
              </p>
            </div>
          )}

          {/* AI search box — centered, with a soft animated glow */}
          <div className="relative">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-1 rounded-full bg-gradient-to-r from-primary/50 via-amber-300/40 to-primary/50 blur-md"
              style={{ animation: 'adocAiGlow 3.2s ease-in-out infinite' }}
            />
            <div className="relative flex items-center gap-3 h-[68px] pl-2.5 pr-5 rounded-full border border-primary/30 dark:border-primary/40 bg-white dark:bg-zinc-900 shadow-sm focus-within:ring-2 focus-within:ring-primary/40 focus-within:border-primary">
              {/* Reference-doc upload — circle-bordered ＋ on the LEFT (thumbnail once attached). */}
              <Tooltip>
                <TooltipTrigger asChild>
                  {sampleImage ? (
                    <button
                      type="button"
                      onClick={() => setSampleImage(null)}
                      aria-label={t('aiDocuments.removeSample', { defaultValue: 'Remove sample' })}
                      className="group relative shrink-0 w-12 h-12 rounded-full overflow-hidden border-2 border-primary/50"
                    >
                      <img src={sampleImage} alt="" className="w-full h-full object-cover" />
                      <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/50 transition-colors">
                        <IconX size={14} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </span>
                    </button>
                  ) : (
                    <label className={`shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-full bg-primary text-white shadow-sm transition-colors ${sampleBusy ? 'opacity-60 pointer-events-none' : 'cursor-pointer hover:bg-primary/90'}`}>
                      {sampleBusy ? <IconLoader2 size={18} className="animate-spin" /> : <IconPlus size={18} stroke={2.5} />}
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        className="hidden"
                        disabled={sampleBusy}
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; onSampleFile(f); }}
                      />
                    </label>
                  )}
                </TooltipTrigger>
                <TooltipContent>
                  {sampleImage
                    ? t('aiDocuments.removeSample', { defaultValue: 'Remove sample' })
                    : t('aiDocuments.uploadSampleHint', { defaultValue: 'Upload a sample document (Ctrl+U) or paste an image (Ctrl+V)' })}
                </TooltipContent>
              </Tooltip>
              <input
                ref={searchInputRef}
                value={chatText}
                onChange={(e) => setChatText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && chatText.trim()) proceedCustom(); }}
                placeholder={t('aiDocuments.describePlaceholder', { defaultValue: 'e.g. A rent agreement for my flat…' })}
                className="flex-1 min-w-0 bg-transparent border-none outline-none text-[17px] text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400"
                autoFocus
              />
              {chatText ? (
                <button
                  onClick={() => setChatText('')}
                  aria-label={t('aiDocuments.clearSearch', { defaultValue: 'Clear' })}
                  className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 shrink-0"
                >
                  <IconX size={16} />
                </button>
              ) : (
                // Keyboard hint — press "/" anywhere to jump into the search box.
                <kbd className="hidden sm:inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-md border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-zinc-400 text-xs font-medium shrink-0 select-none">/</kbd>
              )}
            </div>
          </div>

          {/* Sample uploaded → proceed straight from it (typing is optional) */}
          {sampleImage && (
            <button
              onClick={proceedCustom}
              disabled={planning}
              className="mt-4 w-full text-left flex items-center gap-4 p-4 rounded-2xl border border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors disabled:opacity-60"
            >
              <img src={sampleImage} alt="" className="w-12 h-12 rounded-lg object-cover border border-primary/30 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">
                  {t('aiDocuments.createFromSample', { defaultValue: 'Create from your uploaded sample' })}
                </span>
                <span className="block text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                  {chatText.trim()
                    ? `“${chatText.trim()}”`
                    : t('aiDocuments.createFromSampleHint', { defaultValue: 'AI will reproduce this form for you — no need to type anything.' })}
                </span>
              </span>
              {planning ? <IconLoader2 size={20} className="animate-spin text-primary shrink-0" /> : <IconArrowRight size={20} className="text-primary shrink-0" />}
            </button>
          )}

          {/* Quick-start mode badges — Create focuses the box; the image modes open the
              reference-image → document flow. */}
          {!chatText.trim() && !sampleImage && (
            <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
              {[
                { key: 'create', Icon: IconWand, label: t('aiDocuments.modeCreate', { defaultValue: 'Create document' }), mode: 'text' },
                { key: 'image', Icon: IconPhotoPlus, label: t('aiDocuments.modeImageDoc', { defaultValue: 'Image to document' }), mode: 'image', brief: '' },
                { key: 'restore', Icon: IconRefresh, label: t('aiDocuments.modeRestore', { defaultValue: 'Restore old document' }), mode: 'image', brief: t('aiDocuments.briefRestore', { defaultValue: 'Recreate this old / worn document exactly as a clean, editable copy.' }) },
                { key: 'qpaper', Icon: IconFileText, label: t('aiDocuments.modeQuestionPaper', { defaultValue: 'Handwritten question paper' }), mode: 'image', brief: t('aiDocuments.briefQuestionPaper', { defaultValue: 'Reproduce this handwritten question paper as a clean, printable question paper.' }) },
              ].map(({ key, Icon, label, mode, brief }) => (
                <button
                  key={key}
                  type="button"
                  disabled={planning || sampleBusy}
                  onClick={() => onModeBadge(mode, brief)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-primary/5 border border-primary/30 text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
                >
                  <Icon size={15} />
                  {label}
                </button>
              ))}
              <input
                ref={badgeFileRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (badgeBrief.current) setChatText(badgeBrief.current); onSampleFile(f); }}
              />
            </div>
          )}

          {/* Popular type chips removed per request — the quick-start mode badges above replace them. */}

          {/* Document language — toggle commented out per request (doc language defaults to UI language) */}
          {false && !chatText.trim() && (
            <div className="flex items-center justify-center gap-2 mt-5">
              <span className="text-[11px] font-medium text-zinc-400">{t('aiDocuments.docLanguage', { defaultValue: 'Document language' })}</span>
              <div className="flex items-center gap-1 rounded-lg border border-zinc-200 dark:border-zinc-700 p-0.5">
                <button type="button" onClick={() => setDocLang('bn')} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${docLang === 'bn' ? 'bg-primary text-white' : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'}`}>বাংলা</button>
                <button type="button" onClick={() => setDocLang('en')} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${docLang === 'en' ? 'bg-primary text-white' : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'}`}>EN</button>
              </div>
            </div>
          )}

          {/* Query typed, catalog still loading */}
          {chatText.trim() && !sampleImage && !catalog && (
            <div className="py-12 flex justify-center">
              <IconLoader2 size={26} className="animate-spin text-primary" />
            </div>
          )}

          {/* Template-set matches — presented as ordinary document results (the user never
              sees WHICH engine builds their document; these are simply faster). */}
          {chatText.trim() && !sampleImage && readyTemplates.length > 0 && (
            <div className="mt-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {readyTemplates.map((ts, idx) => (
                  <button
                    key={ts.id}
                    onClick={() => pickReadyTemplate(ts)}
                    disabled={planning || !!pickingSetId}
                    style={{ animation: `adocRise .55s cubic-bezier(0.22,1,0.36,1) ${idx * 90}ms both` }}
                    className="group text-left flex items-center gap-3.5 p-4 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 hover:border-primary/50 hover:bg-primary/5 transition-colors disabled:opacity-60"
                  >
                    <span className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 text-primary shrink-0">
                      {pickingSetId === ts.id ? <IconLoader2 size={26} className="animate-spin" /> : <IconFileText size={30} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[15px] font-semibold text-zinc-900 dark:text-zinc-100 truncate">{ts.name}</span>
                    </span>
                    <IconArrowRight size={18} className="text-zinc-300 group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* AI fallback — always offered under the ready templates (or alone when none match) */}
          {chatText.trim() && !sampleImage && (
            <button
              onClick={proceedCustom}
              disabled={planning}
              style={{ animation: 'adocRise .55s cubic-bezier(0.22,1,0.36,1) both' }}
              className="mt-5 w-full text-left flex items-center gap-4 p-5 rounded-2xl border border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors disabled:opacity-60"
            >
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/15 text-primary shrink-0">
                {planning ? <IconLoader2 size={26} className="animate-spin" /> : <IconSparkles size={26} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">
                  {t('aiDocuments.customCreateTitle', { defaultValue: 'Create this document' })}
                </span>
                <span className="block text-sm text-zinc-600 dark:text-zinc-300 mt-0.5 truncate">“{chatText.trim()}”</span>
                <span className="block text-xs text-zinc-400 mt-1">
                  {t('aiDocuments.customCreateHint', { defaultValue: 'AI will draft it for you in minutes.' })}
                </span>
              </span>
              <IconArrowRight size={20} className="text-primary shrink-0" />
            </button>
          )}
        </div>
      )}

      {/* ── Step 2: upload → centered scan → document glides left, fields slide in ── */}
      {step === 1 && (() => {
        const num = (n) => (isBnUi ? toBanglaDigits(n) : n);
        const filledOf = (list) => list.filter((f) => String(fields[f.id] || '').trim()).length;
        const uploadedSources = displaySources.filter((s) => sourceImages[s.id]);
        const pendingSources = displaySources.filter((s) => !sourceImages[s.id]);
        // A lone, not-yet-uploaded document gets the big centered drop zone (same hero feel
        // as step 1's search box); several pending ones become compact rows in a grid.
        const heroDrop = pendingSources.length === 1 && uploadedSources.length === 0;
        const indexOf = (s) => displaySources.findIndex((d) => d.id === s.id);
        const dismissBtn = (sid, extra = '') => (
          <button
            type="button"
            onClick={() => setDismissedSources((s) => new Set(s).add(sid))}
            aria-label={t('aiDocuments.dismissSource', { defaultValue: 'Remove this document' })}
            title={t('aiDocuments.dismissSource', { defaultValue: 'Remove this document' })}
            className={`w-7 h-7 inline-flex items-center justify-center rounded-full text-zinc-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors shrink-0 ${extra}`}
          >
            <IconX size={15} />
          </button>
        );
        const fillFromDocLink = (emptyList) => (
          <label className={`inline-flex items-center gap-1.5 text-xs font-medium text-primary shrink-0 ${fillBusy ? 'opacity-50 pointer-events-none' : 'cursor-pointer hover:underline'}`}>
            {fillBusy ? <IconLoader2 size={14} className="animate-spin" /> : <IconCloudUpload size={14} />}
            {t('aiDocuments.fillFromDoc', { defaultValue: 'Fill remaining from a document' })}
            <input type="file" accept="image/*,application/pdf" className="hidden" disabled={fillBusy} onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ''; onSourceFiles(files, { id: 'fill', fillFields: emptyList }); }} />
          </label>
        );
        const filledPill = (list) => {
          const done = filledOf(list);
          const all = done === list.length;
          return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0 ${all ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400'}`}>
              {all && <IconCircleCheckFilled size={12} />}
              {t('aiDocuments.filledCount', { filled: num(done), total: num(list.length), defaultValue: '{{filled}}/{{total}} filled' })}
            </span>
          );
        };

        return (
        <div className="max-w-5xl mx-auto space-y-3">
          {/* Header — light, card-less, same airy feel as the search step */}
          <div className="flex items-center gap-3 px-1" style={{ animation: 'adocRise .45s ease-out both' }}>
            <span className="relative inline-flex items-center justify-center w-11 h-11 rounded-xl bg-primary/10 text-primary shrink-0">
              <span aria-hidden="true" className="pointer-events-none absolute -inset-1 rounded-xl bg-primary/20 blur-md" style={{ animation: 'adocAiGlow 3.2s ease-in-out infinite' }} />
              <span className="relative inline-flex"><DocTypeIcon typeId={selectedType} size={28} /></span>
            </span>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-zinc-800 dark:text-zinc-100 truncate">
                {docTypeName || t('aiDocuments.title', { defaultValue: 'AI Document' })}
              </h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                {sampleImage
                  ? t('aiDocuments.scanHintDocs', { defaultValue: 'Upload the documents below — we read the info for you.' })
                  : t('aiDocuments.scanHint', { defaultValue: 'Upload your NID and we read the info for you.' })}
              </p>
            </div>
            {/* Start a fresh document (clears the resumed draft) */}
            <button
              type="button"
              onClick={startOver}
              title={t('aiDocuments.newDocument', { defaultValue: 'New document' })}
              className="ml-auto shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-600 dark:text-zinc-300 text-sm font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
            >
              <IconPlus size={15} />
              <span className="hidden sm:inline">{t('aiDocuments.newDocument', { defaultValue: 'New document' })}</span>
            </button>
          </div>

          {/* Reference image (left, sticky) + the required documents (right) */}
          <div className={sampleImage ? 'flex flex-col lg:flex-row gap-3' : ''}>
            {sampleImage && (
              <div className={`shrink-0 transition-[width] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${plan ? 'lg:w-60' : 'lg:w-[440px]'}`} style={{ animation: 'adocSlideL .5s cubic-bezier(0.22,1,0.36,1) both' }}>
                <div className="lg:sticky lg:top-4 rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-2.5">
                  <p className="text-[11px] font-medium text-zinc-400 mb-1.5 px-0.5">{t('aiDocuments.yourReference', { defaultValue: 'Your reference document' })}</p>
                  <div className="relative rounded-xl overflow-hidden bg-zinc-50 dark:bg-zinc-800">
                    <img src={sampleImage} alt="" className="w-full h-auto object-contain" />
                    {/* While the plan loads, the scanner runs over the reference so it's clear the AI is reading it. */}
                    {!plan && <ScanOverlay />}
                  </div>
                </div>
              </div>
            )}

            <div className="flex-1 min-w-0 space-y-3">
            {!plan ? (
              // Plan still loading — analysing/preparing state (Generate stays disabled meanwhile).
              <AnalysingPanel key={planStage} t={t} reference={!!sampleImage || planStage === 'fields'} stage={planStage} />
            ) : (displaySources.length > 0 || sharedFields.length > 0) ? (
              <>
                {/* Uploaded documents — centered while scanning, then glide left with the
                    extracted fields sliding in on the right. */}
                {uploadedSources.map((source) => {
                  const sIdx = indexOf(source);
                  const uploaded = sourceImages[source.id];
                  const isScanning = extracting === source.id;
                  // Optional second image (NID front/back on separate photos) — its own
                  // upload/scan slot keyed off the source id; OCR merges into `fields`.
                  const secondId = `${source.id}__2`;
                  const secondImg = sourceImages[secondId];
                  const secondScanning = extracting === secondId;
                  // "Other side" is single-person only; multi-person docs give each person a slot.
                  const canSecond = !isMultiPerson && source.kind === 'nid';
                  // Fields beside THIS document: the person's own fields (multi-person) or all.
                  const cardFields = Array.isArray(source.fills) && source.fills.length
                    ? docFields.filter((ff) => source.fills.includes(ff.id))
                    : (isMultiPerson ? docFields.filter((ff) => ff.person === source.person) : docFields);
                  const readCount = (extractedBySource[source.id] || []).length;
                  const title = isMultiPerson || (source.kind && source.kind !== 'nid')
                    ? source.label
                    : t('aiDocuments.yourInfo', { defaultValue: 'Your information' });
                  return (
                    <div
                      key={source.id}
                      className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-3 sm:p-4 shadow-sm"
                      style={{ animation: 'adocRise .45s cubic-bezier(0.22,1,0.36,1) both' }}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        {isMultiPerson && <span className="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-primary/10 text-primary text-[11px] font-bold shrink-0">{num(sIdx + 1)}</span>}
                        <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 truncate">{title}</p>
                        {!isScanning && readCount > 0 && (
                          <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary shrink-0">
                            <IconSparkles size={12} />
                            {t('aiDocuments.fieldsRead', { n: num(readCount), defaultValue: '{{n}} read by AI' })}
                          </span>
                        )}
                        <span className="ml-auto" />
                        {!isScanning && cardFields.length > 0 && filledPill(cardFields)}
                        {dismissBtn(source.id)}
                      </div>

                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        {/* The document on the left — scanner over it while it is read (same
                            layout as the reference-document analysis) */}
                        <div className={`shrink-0 w-full max-w-[300px] mx-auto md:mx-0 md:max-w-none transition-[width] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${isScanning ? 'md:w-[360px]' : 'md:w-[240px]'}`}>
                          <div className={`relative rounded-xl overflow-hidden border bg-zinc-50 dark:bg-zinc-800 transition-shadow duration-500 ${isScanning ? 'border-primary/40 shadow-lg shadow-primary/10' : 'border-zinc-200 dark:border-zinc-700'}`}>
                            <img src={uploaded} alt="" className="w-full h-auto object-contain" />
                            {isScanning && <ScanOverlay />}
                          </div>

                          {!isScanning && canSecond && (secondImg ? (
                            <div className="mt-2.5" style={{ animation: 'adocFieldIn .4s ease-out both' }}>
                              <div className="relative rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800">
                                <img src={secondImg} alt="" className="w-full h-auto object-contain" />
                                {secondScanning && <ScanOverlay />}
                              </div>
                              <p className="mt-1 text-[11px] text-zinc-400 text-center">{t('aiDocuments.otherSide', { defaultValue: 'Other side' })}</p>
                            </div>
                          ) : (
                            // Add ANOTHER photo to scan for this source (e.g. the other side of the NID).
                            <label
                              className={`mt-2.5 flex items-center justify-center gap-1.5 w-full py-2 rounded-xl border border-dashed text-xs font-medium transition-colors ${
                                scanBusy
                                  ? 'opacity-50 pointer-events-none border-zinc-200 dark:border-zinc-700 text-zinc-400'
                                  : 'border-primary/40 text-primary cursor-pointer hover:bg-primary/5'
                              }`}
                              style={{ animation: 'adocFieldIn .4s ease-out .3s both' }}
                            >
                              <IconPlus size={14} stroke={2.5} />
                              {t('aiDocuments.addMoreScan', { defaultValue: 'Add another photo to scan' })}
                              <input
                                type="file"
                                accept="image/*,application/pdf"
                                className="hidden"
                                disabled={scanBusy}
                                onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ''; onSourceFiles(files, { id: secondId }); }}
                              />
                            </label>
                          ))}
                        </div>

                        {/* While scanning: the analysing panel (steps + a preview of this card's
                            fields). Once done: the extracted info slides in from the right. */}
                        {isScanning && (
                          <div className="flex-1 min-w-0">
                            <AnalysingPanel t={t} variant="scan" bare previewLabels={cardFields.map((ff) => ff.label)} />
                          </div>
                        )}
                        {!isScanning && (
                          <div className="flex-1 min-w-0 space-y-2.5" style={{ animation: 'adocPanelIn .6s cubic-bezier(0.22,1,0.36,1) .2s both' }}>
                            {secondScanning && (
                              <p className="inline-flex items-center gap-1.5 text-xs font-medium text-primary">
                                <IconLoader2 size={13} className="animate-spin" />
                                {t('aiDocuments.scanning', { defaultValue: 'Reading document…' })}
                              </p>
                            )}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {cardFields.map((f, i) => (
                                <FieldRow key={f.id} f={f} index={i} value={fields[f.id]} onChange={setField} isBnUi={isBnUi} />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Documents still to upload */}
                {pendingSources.length > 0 && (heroDrop ? (
                  (() => {
                    const source = pendingSources[0];
                    return (
                      <div className="relative max-w-xl mx-auto pt-4" style={{ animation: 'adocRise .55s cubic-bezier(0.22,1,0.36,1) both' }}>
                        <div aria-hidden="true" className="pointer-events-none absolute -inset-1 top-3 rounded-[1.4rem] bg-gradient-to-r from-primary/40 via-amber-300/30 to-primary/40 blur-md" style={{ animation: 'adocAiGlow 3.2s ease-in-out infinite' }} />
                        <label
                          className={`relative flex flex-col items-center justify-center gap-2 px-6 py-10 rounded-[1.25rem] border-2 border-dashed border-primary/50 bg-white dark:bg-zinc-900 text-center transition-colors ${
                            scanBusy ? 'opacity-60 pointer-events-none' : 'cursor-pointer hover:bg-primary/5'
                          }`}
                        >
                          <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary text-white shadow-md shadow-primary/30 mb-1" style={{ animation: 'adocIconBob 2.6s ease-in-out infinite' }}>
                            <IconCloudUpload size={26} />
                          </span>
                          <span className="text-base font-semibold text-zinc-800 dark:text-zinc-100">{source.label}</span>
                          <span className="text-xs text-zinc-500 dark:text-zinc-400">{t('aiDocuments.uploadHint', { defaultValue: 'Upload a photo of the document' })}</span>
                          <span className="mt-1 text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-full bg-primary/10 text-primary">JPG · PNG · PDF</span>
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            className="hidden"
                            disabled={scanBusy}
                            onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ''; onSourceFiles(files, source); }}
                          />
                        </label>
                        {dismissBtn(source.id, 'absolute top-6 right-2 z-10')}
                      </div>
                    );
                  })()
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {pendingSources.map((source, pIdx) => {
                      const sIdx = indexOf(source);
                      return (
                        <div key={source.id} className="relative" style={{ animation: `adocRise .5s cubic-bezier(0.22,1,0.36,1) ${pIdx * 80}ms both` }}>
                          <label
                            className={`group flex items-center gap-3 p-3 pr-10 rounded-2xl border border-primary/30 dark:border-primary/40 bg-white dark:bg-zinc-900 shadow-sm transition-colors ${
                              scanBusy ? 'opacity-60 pointer-events-none' : 'cursor-pointer hover:border-primary hover:bg-primary/5'
                            }`}
                          >
                            <span className="relative inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary shrink-0 group-hover:bg-primary group-hover:text-white transition-colors">
                              <IconCloudUpload size={20} />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-semibold text-zinc-800 dark:text-zinc-100 truncate">
                                {isMultiPerson && <span className="text-primary">{num(sIdx + 1)}. </span>}
                                {source.label}
                              </span>
                              <span className="block text-[11px] text-zinc-400 truncate">
                                {t('aiDocuments.uploadHint', { defaultValue: 'Upload a photo of the document' })} · JPG, PNG, PDF
                              </span>
                            </span>
                            <input
                              type="file"
                              accept="image/*,application/pdf"
                              className="hidden"
                              disabled={scanBusy}
                              onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ''; onSourceFiles(files, source); }}
                            />
                          </label>
                          {dismissBtn(source.id, 'absolute top-1/2 -translate-y-1/2 right-2')}
                        </div>
                      );
                    })}
                  </div>
                ))}

                {/* Shared "other details" — compact tile grid */}
                {sharedFields.length > 0 && (
                  <div
                    className={`rounded-2xl border bg-white dark:bg-zinc-900 p-3 sm:p-4 shadow-sm transition-[border-color,box-shadow] duration-300 ${fillBusy ? 'border-primary/50 ring-4 ring-primary/10' : 'border-zinc-200 dark:border-zinc-700'}`}
                    style={{ animation: 'adocRise .5s cubic-bezier(0.22,1,0.36,1) .1s both' }}
                  >
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-primary/10 text-primary shrink-0"><IconListDetails size={14} /></span>
                      <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">{t('aiDocuments.otherDetails', { defaultValue: 'Other details' })}</p>
                      {filledPill(sharedFields)}
                      <span className="ml-auto" />
                      {!fillBusy && sharedFields.some((f) => !String(fields[f.id] || '').trim()) && fillFromDocLink(sharedFields.filter((f) => !String(fields[f.id] || '').trim()))}
                    </div>
                    {/* Supporting-document scan in progress — the document with the scanner over it */}
                    {fillBusy && (
                      <div className="mb-3 flex items-center gap-3 p-2.5 rounded-xl border border-primary/30 bg-primary/5" style={{ animation: 'adocFieldIn .35s ease-out both' }}>
                        {fillPreview && (
                          <div className="relative w-20 h-14 rounded-lg overflow-hidden border border-primary/30 bg-zinc-50 dark:bg-zinc-800 shrink-0">
                            <img src={fillPreview} alt="" className="w-full h-full object-cover" />
                            <ScanOverlay small />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="flex items-center gap-1.5 text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                            <IconSparkles size={15} className="text-primary animate-pulse shrink-0" />
                            {t('aiDocuments.scanning', { defaultValue: 'Reading document…' })}
                          </p>
                          <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{t('aiDocuments.fillingEmpty', { defaultValue: 'Filling the empty fields from your document' })}</p>
                          <IndeterminateBar className="w-full max-w-[220px] mt-1.5" />
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {sharedFields.map((f, i) => (
                        <FieldRow key={f.id} f={f} index={i} value={fields[f.id]} onChange={setField} isBnUi={isBnUi} pending={fillBusy} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4 text-sm text-zinc-500 dark:text-zinc-400 flex items-center gap-2">
                <IconSparkles size={16} className="text-primary shrink-0" />
                {t('aiDocuments.noUploadNeeded', { defaultValue: 'No document upload needed — AI will draft this from your selection.' })}
              </div>
            )}
            </div>{/* /required-documents column */}
          </div>

          {/* Extraction confirm message */}
          {scannedAny && !extracting && (
            <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 px-3.5 py-2.5 flex items-center gap-2.5" style={{ animation: 'adocFieldIn .4s ease-out both' }}>
              <IconCircleCheckFilled size={18} className="text-emerald-500 shrink-0" />
              <p className="text-xs sm:text-sm text-emerald-700 dark:text-emerald-300">
                {t('aiDocuments.extractConfirm', { defaultValue: 'This information was extracted from your document. Proceed? Press Generate to create the document with it.' })}
              </p>
            </div>
          )}
          <div className={`${footerWideCls} justify-between`}>
            <Btn variant="ghost" className={footerGhostCls} leftIcon={<IconArrowLeft size={16} />} onClick={() => setStep(0)}>
              {t('aiDocuments.back', { defaultValue: 'Back' })}
            </Btn>
            <div className="flex items-center gap-3">
              {/* Scan is optional — let the user proceed and fill fields manually. */}
              {displaySources.length > 0 && !anyUploaded && !skipScan && !sampleImage && (
                <button
                  type="button"
                  onClick={() => setSkipScan(true)}
                  className="text-sm font-medium text-zinc-300 hover:text-white underline-offset-2 hover:underline"
                >
                  {t('aiDocuments.skipScan', { defaultValue: "Skip — I'll fill in manually" })}
                </button>
              )}
              <Btn
                size="lg"
                onClick={() => runFinalize(false)}
                loading={generating}
                disabled={genDisabled}
                leftIcon={<IconWand size={16} />}
                title={scanBusy ? t('aiDocuments.waitScan', { defaultValue: 'Please wait — reading the document…' }) : (needsUpload && !anyUploaded ? t('aiDocuments.uploadFirst', { defaultValue: 'Upload your document first.' }) : undefined)}
              >
                {t('aiDocuments.generate', { defaultValue: 'Generate' })}
              </Btn>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── Step 3: Document (canvas preview + settings-style right sidebar) ── */}
      {step === 2 && (() => {
        const genBusy = generating && docBlocks.length === 0 && docHtml == null;
        const toolsOff = editingIdx < 0 || genBusy;
        const activeBlock = editingIdx >= 0 ? docBlocks[editingIdx] : null;
        const hasDoc = docBlocks.length > 0 || docHtml != null;

        const segTrack = 'flex items-center gap-1 p-1 rounded-xl bg-zinc-100 dark:bg-zinc-800';
        const segBtn = 'flex-1 inline-flex items-center justify-center h-8 rounded-lg text-xs font-medium transition-colors';
        const segOff = 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200';
        const segOn = 'bg-white dark:bg-zinc-900 text-primary shadow-sm';
        const cardBtn = 'flex flex-col items-center justify-center gap-1.5 h-[68px] px-1 rounded-xl bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200/70 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50 disabled:pointer-events-none';
        const subLabel = 'text-[11px] font-medium text-zinc-400';
        const secLabel = 'text-[13px] font-semibold text-zinc-800 dark:text-zinc-200';

        return (
        <div className="space-y-4">
          <div className="flex flex-col lg:flex-row gap-4 items-start">
            <div className="flex-1 min-w-0 w-full">
              {genBusy ? (
                <GeneratingPreview lang={docLang} name={docTypeName} t={t} />
              ) : (
                <DocPreviewCanvas lang={docLang} name={docTypeName} paper={paper} landscape={landscape}>
                  {(zoom) => (docHtml != null ? (
                    <HtmlDocEditor
                      ref={editorRef}
                      html={docHtml}
                      onChange={onHtmlEdit}
                      onEditingChange={setEditingIdx}
                      editable
                      lang={docLang}
                      zoom={zoom}
                      paper={paper}
                      landscape={landscape}
                      globalFont={globalFont}
                      fontScale={globalFontScale}
                      margins={margins}
                      preserveStyles={!!plan?.preserveStyles}
                    />
                  ) : (
                    <BlockEditor
                      ref={editorRef}
                      blocks={docBlocks}
                      onChange={onBlockEdit}
                      onEditingChange={setEditingIdx}
                      editable
                      lang={docLang}
                      zoom={zoom}
                      paper={paper}
                      landscape={landscape}
                      globalFont={globalFont}
                      fontScale={globalFontScale}
                      margins={margins}
                    />
                  ))}
                </DocPreviewCanvas>
              )}
            </div>

            {/* Right sidebar — settings-panel style, sticky with a pinned Print footer */}
            <div className="w-full lg:w-72 shrink-0 lg:sticky lg:top-4">
              <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 overflow-hidden flex flex-col lg:max-h-[calc(100vh-2rem)]">
                {/* Scroll region — everything except the pinned Print button */}
                <div className="lg:flex-1 lg:overflow-y-auto">
                {/* Header */}
                <div className="flex items-center gap-3 p-4">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary shrink-0">
                    <IconSettings size={20} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-zinc-900 dark:text-zinc-100 truncate">
                      {t('aiDocuments.docSettings', { defaultValue: 'Document settings' })}
                    </p>
                    <p className="text-[11px] text-zinc-400 truncate">
                      {t('aiDocuments.docSettingsSub', { defaultValue: 'Page, format & export' })}
                    </p>
                  </div>
                </div>

                {/* Page — paper size + orientation + document-wide font */}
                <div className="px-4 pb-4 pt-3 space-y-3 border-t border-zinc-100 dark:border-zinc-800">
                  <div className="space-y-1.5">
                    <p className={subLabel}>{t('aiDocuments.paperSize', { defaultValue: 'Paper size' })}</p>
                    <CustomSelect
                      value={paper}
                      onChange={setPaper}
                      className="h-11"
                      options={PAPER_SIZES.map((p) => ({ value: p.id, label: `${p.label} · ${p.dim}` }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <p className={subLabel}>{t('aiDocuments.docFont', { defaultValue: 'Document font' })}</p>
                    <FontSelect value={globalFont || ''} onChange={setGlobalFont} isBn={isBnUi} className="h-11" />
                  </div>
                  <div className="space-y-1.5">
                    <p className={subLabel}>{t('aiDocuments.docFontSize', { defaultValue: 'Document font size' })}</p>
                    <CustomSelect
                      value={globalFontScale}
                      onChange={setGlobalFontScale}
                      className="h-11"
                      options={FONT_SIZE_SCALES.map((s) => ({ value: s.value, label: isBnUi ? s.bn : s.en }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <p className={subLabel}>{t('aiDocuments.orientation', { defaultValue: 'Orientation' })}</p>
                    <div className={segTrack}>
                      <button type="button" className={`${segBtn} ${!landscape ? segOn : segOff}`} onClick={() => setLandscape(false)}>{isBnUi ? 'পোর্ট্রেট' : 'Portrait'}</button>
                      <button type="button" className={`${segBtn} ${landscape ? segOn : segOff}`} onClick={() => setLandscape(true)}>{isBnUi ? 'ল্যান্ডস্কেপ' : 'Landscape'}</button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <p className={subLabel}>{t('aiDocuments.margin', { defaultValue: 'Margins' })}</p>
                    <div className={segTrack}>
                      {[
                        ['default', t('aiDocuments.marginDefault', { defaultValue: 'Default' })],
                        ['narrow', t('aiDocuments.marginNarrow', { defaultValue: 'Narrow' })],
                        ['wide', t('aiDocuments.marginWide', { defaultValue: 'Wide' })],
                        ['custom', t('aiDocuments.marginCustom', { defaultValue: 'Custom' })],
                      ].map(([id, label]) => (
                        <button key={id} type="button" className={`${segBtn} ${(!sourceMargins && marginPreset === id) ? segOn : segOff}`} onClick={() => { setSourceMargins(null); setMarginPreset(id); }}>{label}</button>
                      ))}
                    </div>
                    {marginPreset === 'custom' && (
                      <div className="grid grid-cols-4 gap-1.5 pt-1">
                        {[
                          ['top', t('aiDocuments.marginTop', { defaultValue: 'Top' })],
                          ['bottom', t('aiDocuments.marginBottom', { defaultValue: 'Bottom' })],
                          ['left', t('aiDocuments.marginLeft', { defaultValue: 'Left' })],
                          ['right', t('aiDocuments.marginRight', { defaultValue: 'Right' })],
                        ].map(([side, label]) => (
                          <div key={side}>
                            <p className="text-[10px] text-zinc-400 mb-0.5 text-center">{label}</p>
                            <input
                              type="number"
                              min={0}
                              max={70}
                              step={1}
                              value={customMm[side]}
                              onChange={(e) => { setSourceMargins(null); setCustomMm((m) => ({ ...m, [side]: e.target.value === '' ? '' : Math.min(70, Math.max(0, Number(e.target.value))) })); }}
                              className="w-full h-9 px-1 text-center border border-zinc-200 dark:border-zinc-700 rounded-lg text-xs bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                          </div>
                        ))}
                        <p className="col-span-4 text-[10px] text-zinc-400">{t('aiDocuments.marginUnit', { defaultValue: 'Values in mm' })}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Formatting — shown only while a text/paragraph is selected */}
                {!toolsOff ? (
                  <div className="px-4 pb-4 pt-3 space-y-3 border-t border-zinc-100 dark:border-zinc-800">
                    <div className="flex items-center justify-between">
                      <p className={secLabel}>{t('aiDocuments.formatting', { defaultValue: 'Formatting' })}</p>
                      <button
                        type="button"
                        className="text-[11px] font-medium text-primary hover:underline"
                        onMouseDown={(e) => { e.preventDefault(); editorRef.current?.resetMeta(); }}
                      >
                        {t('aiDocuments.reset', { defaultValue: 'Reset' })}
                      </button>
                    </div>

                    {/* Style — B / I / U */}
                    <div className="space-y-1.5">
                      <p className={subLabel}>{t('aiDocuments.styleLabel', { defaultValue: 'Style' })}</p>
                      <div className={segTrack}>
                        <button type="button" title="Bold (Ctrl+B)" className={`${segBtn} ${segOff}`} onMouseDown={(e) => { e.preventDefault(); editorRef.current?.exec('bold'); }}><IconBold size={15} /></button>
                        <button type="button" title="Italic (Ctrl+I)" className={`${segBtn} ${segOff}`} onMouseDown={(e) => { e.preventDefault(); editorRef.current?.exec('italic'); }}><IconItalic size={15} /></button>
                        <button type="button" title="Underline (Ctrl+U)" className={`${segBtn} ${segOff}`} onMouseDown={(e) => { e.preventDefault(); editorRef.current?.exec('underline'); }}><IconUnderline size={15} /></button>
                      </div>
                    </div>

                    {/* Alignment */}
                    <div className="space-y-1.5">
                      <p className={subLabel}>{t('aiDocuments.alignment', { defaultValue: 'Alignment' })}</p>
                      <div className={segTrack}>
                        {[
                          { a: 'left', Icon: IconAlignLeft },
                          { a: 'center', Icon: IconAlignCenter },
                          { a: 'right', Icon: IconAlignRight },
                        ].map(({ a, Icon }) => (
                          <button
                            key={a}
                            type="button"
                            title={a}
                            className={`${segBtn} ${activeBlock?.align === a ? segOn : segOff}`}
                            onMouseDown={(e) => { e.preventDefault(); editorRef.current?.setAlign(a); }}
                          >
                            <Icon size={15} />
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Font */}
                    <div className="space-y-1.5">
                      <p className={subLabel}>{t('aiDocuments.font', { defaultValue: 'Font' })}</p>
                      <FontSelect
                        value={activeBlock?.font || ''}
                        onChange={(id) => editorRef.current?.setFont(id)}
                        isBn={isBnUi}
                        className="h-11"
                      />
                    </div>

                    <p className="text-[11px] text-zinc-400">
                      {t('aiDocuments.editingHint', { defaultValue: 'Formatting applies to the selected text/paragraph.' })}
                    </p>
                  </div>
                ) : (
                  <div className="px-4 pb-4 pt-3 border-t border-zinc-100 dark:border-zinc-800">
                    <p className={`${secLabel} mb-1.5`}>{t('aiDocuments.formatting', { defaultValue: 'Formatting' })}</p>
                    <p className="text-[11px] text-zinc-400">
                      {t('aiDocuments.clickToEdit', { defaultValue: 'Click any text in the document to edit it.' })}
                    </p>
                  </div>
                )}

                {/* Actions — Regenerate / Copy / PDF are secondary (Print is the pinned footer) */}
                <div className="px-4 pb-4 pt-3 space-y-2.5 border-t border-zinc-100 dark:border-zinc-800">
                  <p className={secLabel}>{t('aiDocuments.actions', { defaultValue: 'Actions' })}</p>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      className={cardBtn}
                      onClick={() => runFinalize(true)}
                      disabled={manualEditing || generating || !hasDoc}
                      title={manualEditing ? t('aiDocuments.regenLocked', { defaultValue: 'Edited by hand — regeneration disabled' }) : undefined}
                    >
                      {generating && hasDoc ? <IconLoader2 size={18} className="animate-spin" /> : <IconRefresh size={18} />}
                      <span className="text-[10px] font-medium text-center leading-tight">{t('aiDocuments.regenerate', { defaultValue: 'Regenerate' })}</span>
                    </button>
                    <button type="button" className={cardBtn} onClick={copyText} disabled={!hasDoc}>
                      <IconCopy size={18} />
                      <span className="text-[10px] font-medium text-center leading-tight">{t('aiDocuments.copy', { defaultValue: 'Copy' })}</span>
                    </button>
                    <button
                      type="button"
                      className={cardBtn}
                      onClick={handleDownloadPdf}
                      disabled={!hasDoc || pdfing}
                    >
                      {pdfing ? <IconLoader2 size={18} className="animate-spin" /> : <IconDownload size={18} />}
                      <span className="text-[10px] font-medium text-center leading-tight">{t('aiDocuments.downloadPdf', { defaultValue: 'Download PDF' })}</span>
                    </button>
                  </div>
                  {/* Advanced edit — open this document in the shared PDF editor for precise layout. */}
                  <button
                    type="button"
                    className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-xl text-[12px] font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200 hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-60 disabled:pointer-events-none"
                    onClick={openAdvancedEdit}
                    disabled={!hasDoc || advBuilding}
                  >
                    {advBuilding ? <IconLoader2 size={16} className="animate-spin" /> : <IconPencil size={16} />}
                    {t('aiDocuments.advancedEdit', { defaultValue: 'Advanced edit (PDF editor)' })}
                  </button>
                </div>
                </div>{/* /scroll region */}

                {/* Pinned Print button — green, always visible without scrolling */}
                <div className="shrink-0 p-3 border-t border-zinc-100 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                  <button
                    type="button"
                    className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-semibold bg-[#34A853] hover:bg-[#2C9247] text-white shadow-sm transition-colors disabled:opacity-60 disabled:pointer-events-none"
                    onClick={handlePrint}
                    disabled={!hasDoc || printing}
                  >
                    {printing ? <IconLoader2 size={16} className="animate-spin" /> : <IconPrinter size={16} />}
                    {printing ? t('aiDocuments.preparing', { defaultValue: 'Preparing…' }) : t('aiDocuments.print', { defaultValue: 'Print' })}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Footer — available once the document is ready: start a fresh one, or
              finish (saves any inline edits) and return to step 1 for a new document. */}
          {hasDoc && (
            <div className={`${footerCls} justify-between`}>
              <Btn variant="ghost" onClick={startOver} className={footerGhostCls}>{t('aiDocuments.startOver', { defaultValue: 'Start over' })}</Btn>
              <div className="flex items-center gap-2">
                {edited && (
                  <span className="hidden sm:inline text-[11px] text-zinc-300 mr-1">{t('aiDocuments.unsavedChanges', { defaultValue: 'Unsaved changes.' })}</span>
                )}
                <Btn size="lg" onClick={finishAndNew} leftIcon={<IconCheck size={16} />}>
                  {t('aiDocuments.finish', { defaultValue: 'Finish' })}
                </Btn>
              </div>
            </div>
          )}
        </div>
        );
      })()}

      {advDoc && (
        <PdfEditorModal
          open={advOpen}
          file={advDoc.file}
          initialObjects={advDoc.objects}
          initialDirty={false}
          documentMode
          externalLoading={advLoading}
          onClose={onAdvancedClose}
          onDone={onAdvancedDone}
        />
      )}
      <ScanCropDialog
        open={cropState.open}
        imageSrc={cropState.imageSrc}
        initialCrop={cropState.initialCrop}
        lang={docLang}
        busy={cropState.open && !cropState.imageSrc}
        onCancel={() => setCropState({ open: false, imageSrc: null, initialCrop: null, sourceId: null, person: null, fillFields: null, forSample: false })}
        onConfirm={onCropConfirm}
      />
      <LimitReachedModal opened={limitOpen} onClose={() => setLimitOpen(false)} isOperator={isOperator} />
    </div>
  );
}
